'use strict';

define('vb/private/stateManagement/container',[
  'ojs/ojcontext',
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/action/actionChainRunner',
  'vb/private/action/assignmentHelper',
  'vb/private/stateManagement/storeManager',
  'vb/private/stateManagement/scope',
  'vb/private/stateManagement/context/withEnvContext',
  'vb/private/stateManagement/router',
  'vb/private/stateManagement/routerUtils',
  'vb/private/stateManagement/rules/rulesEngine',
  'vb/private/log',
  'vbc/private/logConfig',
  'vb/private/stateManagement/stateUtils',
  'vb/private/stateManagement/extensionViewModel',
  'vb/private/translations/bundlesModel',
  'vb/private/translations/bundleUtils',
  'vb/private/constants',
  'vb/private/utils',
  'vb/errors/httpError',
  'vb/private/helpers/eventHelper',
  'vb/private/stateManagement/scopeResolver',
  'vb/private/model/modelUtils',
  'vb/private/events/eventRegistry',
  'vb/private/configLoader',
  'vb/private/debug/applicationDebugStream',
  'vbc/private/performance/performance',
  'vb/private/events/eventMonitorOptions',
  'vb/private/events/eventPayloadMetadata',
  'vb/private/stateManagement/vbWebSocket',
  'urijs/URI',
], (ojContext, VbRouter, ActionChainRunner, AssignmentHelper, StoreManager, Scope,
  WithEnvContext, Router, RouterUtils, RulesEngine, Log, LogConfig, StateUtils, ExtensionViewModel,
  BundlesModel, BundleUtils, Constants, Utils, HttpError, EventHelper, ScopeResolver,
  ModelUtils, EventRegistry, ConfigLoader, ApplicationDebugStream, Performance,
  EventMonitorOptions, EventPayloadMetadata, VbWebSocket, URI) => {
  const logger = Log.getLogger('/vb/stateManagement/container', [
    // Register custom loggers
    {
      name: 'beforeHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerStart,
    },
    {
      name: 'afterHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerEnd,
    },
  ]);

  /**
   * @typedef {import('vb/private/stateManagement/page').default} Page
   * @typedef {import('vb/private/stateManagement/flow').default} Flow
   * @typedef {import('vb/private/stateManagement/layout').default} Layout
   * @typedef {import('vb/private/stateManagement/fragment').default} Fragment
   * @typedef {import('vb/private/stateManagement/application').default} Application
   * @typedef {import('vb/private/stateManagement/fragmentBridge').default} FragmentBridge
   * @typedef {import('vb/private/stateManagement/appPackage').default} AppPackage
   * @typedef {import('vb/private/vx/extension').default} Extension
   * @typedef {import('vb/private/events/eventBehavior').default} EventBehavior
   * @typedef {import('vb/private/events/eventModel').default} EventModel
   * @typedef {import('vb/private/services/services').default} Services
   * @typedef {typeof import('vb/private/history').default} VbHistory
   * @typedef {import('vb/private/stateManagement/navigationContext').default} NavigationContext
   * @typedef {import('vb/private/stateManagement/router').NavigateResult} NavigateResult
   * @typedef {import('vb/private/stateManagement/router').RootRouter} RootRouter
   * @typedef {Application & { alwaysRethrowException?: boolean }} ApplicationWithRethrowFlag
   * @typedef {typeof import('vb/private/stateManagement/context/containerContext').default} ContainerContextType
   */

  const EXTENSIONID_FUNCTIONS_RE = /^[A-Za-z][A-Za-z0-9_+.-]*:\$functions\/[A-Za-z][A-Za-z0-9_+.-]*/;
  const FUNCTIONS_FOLDER_RE = /^\$functions\/[A-Za-z][A-Za-z0-9_+.-]*/;
  const GLOBAL_FUNCTIONS_FOLDER_RE = /^global:\$functions\/[A-Za-z][A-Za-z0-9_+.-]*/;
  const SKIP_MODULE_IMPORT = Symbol('skipModuleImport');
  const NOOP_PROXY_FUNC = () => {
    logger.info('Using no-op method! Check your configuration!');
    return undefined;
  };

  /**
   * Returns true when the ignored event detail has the shape of an action callback bridge event.
   * @param {Object} detail event detail
   * @returns {boolean}
   */
  const isActionCallbackEvent = (detail) => Boolean(detail
    && typeof detail.done === 'function'
    && typeof detail.fail === 'function'
    && typeof detail.name === 'string'
    && detail.params
    && detail.params.action);

  /**
   * Completes callback-style component events that are ignored by the disposed-container guard. This prevents callers
   * that supplied a done callback from waiting indefinitely when the target container can no longer run the listener
   * chain.
   * @param {Object} event event object
   */
  const settleIgnoredCallbackEvent = (event) => {
    const detail = event && event.detail;

    if (detail && typeof detail.done === 'function') {
      try {
        // If a disposed container cannot run an action callback listener, use the same result as the callback bridge's
        // not-registered fallback. This prevents stale external callback registrations from leaving RT action chains
        // pending forever. Other callback-style events are settled only to avoid leaving upstream code waiting.
        if (isActionCallbackEvent(detail)) {
          logger.info('Continuing ignored action callback event on disposed container:', event && event.type);
          detail.done({ continue: true });
        } else {
          logger.info('Settling callback for ignored event on disposed container:', event && event.type);
          detail.done();
        }
      } catch (e) {
        logger.warn('Unable to settle callback for ignored event on disposed container:', event && event.type, e);
      }
    }
  };

  const {
    normalizeEventPayloadRowContextMetadata,
    preserveEventPayloadRowContextMetadata,
  } = EventPayloadMetadata;

  /**
   * Takes a synchronous snapshot of drag data that becomes unavailable after the browser event turn.
   * Browser DataTransfer data is in a protected store; drop payloads can be read during the native drop event,
   * but async action chains may see empty types/data if we keep only the live DataTransfer reference.
   * This intentionally preserves read access for chain code and does not try to make async mutation methods useful.
   * @param {DataTransfer} dataTransfer drag data from a DOM event
   * @returns {Object} dataTransfer wrapper with stable types and getData(type)
   */
  const snapshotDataTransfer = (dataTransfer) => {
    if (typeof dataTransfer.getData !== 'function') {
      return dataTransfer;
    }

    const types = Array.from(dataTransfer.types || []);
    const dataByType = {};

    // getData() is only reliable while handling the synchronous native event, so read every advertised type now.
    types.forEach((type) => {
      try {
        dataByType[type] = dataTransfer.getData(type);
      } catch (e) { // eslint-disable-line no-unused-vars
        dataByType[type] = '';
      }
    });

    // Return an async-readable snapshot, not a DataTransfer clone. This keeps the supported read surface explicit.
    // Avoid inheriting native mutation methods such as setData(), clearData(), or setDragImage(), which cannot usefully
    // affect the browser drag store after the native event turn.
    return {
      types,
      getData: (type) => (Object.prototype.hasOwnProperty.call(dataByType, type) ? dataByType[type] : ''),
      // Keep dropEffect/effectAllowed writable because they are assignable on a real DataTransfer.
      // Assigning them on this async snapshot is only local compatibility; it does not mutate the browser drag store.
      dropEffect: dataTransfer.dropEffect,
      effectAllowed: dataTransfer.effectAllowed,
      files: dataTransfer.files,
      items: dataTransfer.items,
    };
  };

  /**
   * Returns true if constant's value is determined by rules.
   * @param {Object} obj constant definition
   * @returns {boolean}
   */
  const isValueFromRules = (obj) => obj
    && obj[Constants.VariablePropertiesInternal.VALUEFROM] === Constants.VariablePropertyValueFrom.RULES;

  const isValueFromInline = (obj) => obj
    && obj[Constants.VariablePropertiesInternal.VALUEFROM] === Constants.VariablePropertyValueFrom.INLINE;

  // Metadata descriptor types not allowed for a variable
  const INVALID_VARIABLE_METADATA_DESCRIPTOR_TYPES = [
    'vb/MetadataDescriptor',
    'vb/FactoryMetadataProviderDescriptor',
    'vb/JsonMetadataProviderDescriptor',
    'vb/ServiceMetadataProviderDescriptor',
    'vb/DynamicLayoutMetadataProviderDescriptor',
    'vb/ContainerMetadataProviderDescriptor',
    'vb/HeterogeneousMetadataProviderDescriptor',
  ];

  /**
   * Proxy each functions method but make sure it is accessible so only function modules that are listed are callable,
   * and are of type 'function'.
   * @param {*} module
   * @param {object} funcsDef map of functions that need to be proxied. some are excluded
   * @param {string} alias of the functions module
   * @param {string} extId id of the extension
   * @returns {*}
   */
  const PROXY_OVER_FUNC_METHOD = (module, funcsDef, alias, extId) => new Proxy(module, {
    get: (target, key) => {
      // ignore internal symbols. JET's CSP expression evaluator probes Symbol(proxy) to unwrap its own proxies.
      if (typeof key === 'symbol' || key === Constants.SKIP_EXPRESSION_PROXY_SYMBOL) {
        return undefined;
      }

      // allow access only to functions that are listed in the functions.json def. For the current extension
      // every function is automatically available
      const fileFuncsDef = funcsDef || {};
      let excludeAccess = '';
      if (fileFuncsDef[key]) {
        const { methodName } = fileFuncsDef[key];
        excludeAccess = fileFuncsDef[key].excludeAccess;
        if (methodName && typeof module[methodName] === 'function' && !excludeAccess) {
          return Reflect.get(target, methodName);
        }
      }
      const errMsg = excludeAccess || (`A method: ${String(key)} does not exist on the module: ${alias}, from `
        + `extension: ${extId}. A no-op method is being used instead. Please check your configuration!`);
      logger.error(errMsg);
      // this was requested in order to not have expressions involving non-existing methods fail
      return NOOP_PROXY_FUNC;
    },
    set(target, key) {
      throw new Error(`Cannot mutate a function ${String(key)} using an expression!`);
    },
  });

  /**
   * for the event listener, look for an optional property of the given name, and create an
   * expression from its value.
   * If the expression evaluates to true, this method returns true.
   *
   * @param expressionString expression. Return 'defaultValue' value if null
   * @param expressionContexts normal page expression context, plus $event
   * @param defaultValue optional value to return when the expression is null or undefined. defaults to false.
   * @returns {*|boolean}
   */
  const evaluateSynchronousHandlerProperty = (expressionString, expressionContexts, defaultValue = false) => {
    if (expressionString) {
      const expr = StateUtils.getValueOrExpression(expressionString, expressionContexts);
      return Utils.resolveIfObservable(expr);
    }
    return defaultValue;
  };

  /**
   * An error to be return when the container property is not subclassed
   *
   * @param  {String}  propName  the name of the property to be define
   * @return {Error}  the error message.
   */
  const undefinedPropertyError = (propName) => new Error(`Containers must define the ${propName} property.`);

  /**
   * An error to be return when the container method is not subclassed
   *
   * @param  {String}  methodName  the name of the property to be define
   * @return {Error}  the error message.
   */
  const undefinedMethodError = (methodName) => new Error(`Containers must implement ${methodName}.`);

  /**
   * returns true if rules can be used for the container.
   * @param container
   * @returns {boolean}
   */
  const checkRulesSupport = (container) => {
    const opts = container.definition.options || {};
    let hasLoader = false;

    try {
      hasLoader = container.constructor.rulesDescriptorLoaderName;
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      // containers not supporting rules descriptor throw undefinedPropertyError
      hasLoader = false;
    }
    return ((opts[Constants.ContainerModelOptions.CONSTANTS_RULES] === Constants.OptionConstantsRules.ON) && hasLoader);
  };

  /**
   * A base class for containers that require variable and action chain support. Classes in brackets are mixins
   *
   *
   *                                             Container
   *                                                 |
   *           +-------------------------------------+-------------------------------------------+
   *           |                                     |                                           |
   *         Flow                             (FragmentHolder)                         ContainerExtension
   *           |                                     |                                           |
   *     +-----+-----+                +--------------+-------------+                  +----------+----------+
   *     |           |                |              |             |                  |                     |
   * Application PackageFlow         Page         Fragment       Layout        FlowExtension   (FragmentHolderExtension)
   *                 |                |              |                                |                     |
   *             AppPackage    PageInExtension PackageFragment                        |                     |
   *                                  |                                    +----------+---------+           |
   *                              PackagePage                              |                    |           |
   *                                                             ApplicationExtension  AppPackageExtension  |
   *                                                                                                        |
   *                                                                                                        |
   *                                                                      +-----------------+---------------+
   *                                                                      |                 |               |
   *                                                                PageExtension   LayoutExtension  FragmentExtension
   */
  class Container {
    /**
     * Constructs a Container
     *
     * @param  {String}  id
     * @param  {Container}  parent
     * @param  {String} [className='Container']  The class name
     */
    constructor(id, parent, className = 'Container') {
      /**
       * Initialize the static property '__application' with the Application instance
       * That is used for the value the application property for all containers
       */
      if (className === 'Application' && !Container[Constants.APP_INTERNAL_PROPERTY_NAME]) {
        Container[Constants.APP_INTERNAL_PROPERTY_NAME] = this;
      }

      /**
       * @type {String}
       */
      this._id = id;

      /**
       * Hold the class name, 'Application', 'Flow', 'Page', etc...
       * @type {String}
       */
      this._className = className;

      /**
       * Sets the parent container. For a page, it's a flow, for a flow
       * it's a page, for Application it is null.
       * @type {Container}
       */
      this._parent = parent;

      /**
       * For base object application, there is no extension so we use duckTyping of an empty extension.
       * @type {Object}
       * @protected
       */
      this._extension = Container.createEmptyExtension();

      /**
       * Initialize a variable with the page path so we don't recalculate it all the time
       * @type {String}
       */
      this.fullPath = this.getContainerPath();

      /**
       * The path of this container relative to the application,
       * something like: 'flows/main/pages/main-start'
       * Each container implementation is responsible to set this property.
       * @type {String}
       */
      this.path = '';

      /**
       * The scope used to store variables
       * @type {Scope}
       */
      this.scope = null; // Initialized in initVariableScope()

      /**
       * @type {VbRouter}
       */
      this.router = null; // Initialized in initRouter()
      /**
       * The entire set of Context objects, ($application, and $page, and $flow, etc)
       * @type {Object}
       */
      this.availableContexts = null; // Initialized in getAvailableContexts()
      this.expressionContext = null; // the single Context object, for expressions ($page OR $flow OR $chain, etc)
      this.functions = null; // initialized in loadFunctionModule
      this.variablesListeners = [];
      this.chains = {}; // initialized in initializeActionChains
      this.lifecycleState = Constants.ContainerState.CREATED;
      this.log = logger;
      this.eventListeners = {}; // initialized in initializeComponentListeners
      this.enums = {}; // initialized in initializeEnums
      /**
       * Action chains currently running from this container.
       * @type {Set<Promise>}
       */
      this.pendingActionChainPromises = new Set();

      this.bundles = undefined;

      this.viewModelPromise = null; // created in getViewModel
      this.loadFunctionsPromise = null;
      this.loadMetadataPromise = null;
      this.loadDescriptorPromise = null;
      this.loadBundlesPromise = null;

      this.exported = null; // defined in get export()

      // The array of all extension object that apply to this container
      this.extensionsArray = [];

      // A map of all extensions that apply to this container
      this.extensions = {};

      // A map of all imports that apply to this container
      this.imports = {};

      // A map of all modules that apply to this container
      this.modules = {};

      /**
       * When container is an extension, this is a reference to the base object being extended
       * Defined in containerExtension.js
       * @type {Object}
       */
      this.base = undefined;

      /**
       * The metadata loaded from the .json
       * Defined in initDefault as a read-only property after the metadata is loaded
       * @type {Object}
       */
      this.definition = null;

      /**
       * @type {Array<Layout>}
       */
      this.layouts = []; // All the layout used in this container

      this.navPath = null; // defined in getNavPath()

      /**
       * The instances of the loaded flows used by this container (flow and appPackage)
       * @type {Object.<String, Flow>}
       */
      this.flows = {};

      /**
       * Holds functions to unregister customer event when the container is deleted.
       * Also used to clean up the eventHelper created for a functions module
       * @type {Array<function>}
       */
      this.unregisterEventArray = [];

      /**
       * reference to the RulesEngine instance that manages execution of this container's rules.
       * @type {undefined}
       */
      this.rulesEngine = undefined;

      /**
       * the rules descriptor loader Promise
       * @private
       * @type {Promise|null}
       */
      this.loadRulesPromise = null;

      /**
       * A map of all the declared WebSockets
       * @type {Object.<string, VbWebSocket>}
       */
      this.webSockets = {};
    }

    /**
     * Create an empty extension that is used by the extension property definition in the constructor.
     * Every container has an extension property value but for base container that do not extend anything,
     * we needed an object representing a non-extension for the id. baseUrl and getAbsoluteUrl call.
     * It is also used by tests to alter the location of the application by changing the
     * baseUrl property (see TestUtils.js).
     * @return {Object}
     */
    static createEmptyExtension() {
      return {
        // Use "base" for the id of the potential extension of this container
        id: Constants.ExtensionFolders.BASE,
        // ConfigLoader.requirePath is used by tests to change the base url of the app without
        // touching the requirejs baseUrl that point to the tests classes.
        // For an app not in the testing environment, ConfigLoader.requirePath is null
        baseUrl: ConfigLoader.requirePath || '',
        files: [],
        getAbsoluteUrl() {
          return requirejs.toUrl('');
        },
        fileExists() {
          return true;
        },
        extensionResources: {},
      };
    }

    /**
     * @type {String}
     * @readonly
     */
    get id() {
      return this._id;
    }

    /**
     * The default name is the id, subclass change that
     * @type {String}
     */
    get name() {
      return this._id;
    }

    /**
     * @type {String}
     * @readonly
     */
    get className() {
      return this._className;
    }

    /**
     * @type {Container}
     * @readonly
     */
    get parent() {
      return this._parent;
    }

    /**
     * @type {Application}
     * @readonly
     */
    // eslint-disable-next-line class-methods-use-this
    get application() {
      return Container[Constants.APP_INTERNAL_PROPERTY_NAME];
    }

    /**
     * The extension object this resource is coming from.
     *
     * @type {Object}
     */
    get extension() {
      return this._extension;
    }

    /**
     * For application where the extension is empty, the id is 'base'.
     * It is needed to define the path of the object extending this container.
     * It is used to defined
     * @type {String}
     */
    get extensionId() {
      return this.extension.id;
    }

    /**
     * Calculate the base path of the extended resource for this container.
     * @param {String} path
     * @return {String}
     */
    getBasePath(path) {
      return `${this.extensionId}/${path}`;
    }

    /**
     * The path to prefix to the resource path in order to reach it in the extension manager.
     * In the case of an application resource the extension is empty and the baseUrl is '',
     * but for App UI, the baseUrl is the extension base URL
     *
     * @type {String}
     */
    get baseUrl() {
      return this.extension.baseUrl;
    }

    /**
     * @type {AppPackage|undefined}
     */
    // eslint-disable-next-line class-methods-use-this
    get package() {
      return undefined;
    }

    /**
     * The id in the URL is by default the same as the container id
     * @type {!String}
     */
    get urlId() {
      return this._id;
    }

    /**
     * The location of the resources relative to the application.
     * It is used in getResourceFolder() that is used to build the path to load the resource.
     * This value is modified by appPackage where the file structure is different.
     * A page is an application "flows/main/pages/"
     * Same page in an appPackage is "ui/self/applications/appPack1/flows/main/pages/"
     * Note that this is using a getter because the value of path is defined later in the
     * subclass constructor
     * @type {String}
     */
    get resourceLoc() {
      return this.path;
    }

    /**
     * Used to define the value of the path builtin variable on $extension
     * this is to access images resource that are not bundled with the extension.
     * @type {String}
     */
    get absoluteUrl() {
      return this.extension.getAbsoluteUrl();
    }

    /**
     * @type {String}
     * @readonly
     */
    get eventPrefix() {
      // By default the event prefix is the class
      return this._className.toLowerCase();
    }

    /**
     * The name of the span used when loading the resource
     * Names can only be ones of the list in MonitorOptions class
     *
     * @type {String}
     */
    get loadSpanName() {
      return `${this.className}Load`;
    }

    /**
     * The name of the span used when variable are activated
     * Names can only be ones of the list in MonitorOptions class
     *
     * @type {String}
     */
    get activateSpanName() {
      return `${this.className}Activate`;
    }

    /**
     * Subclasses can override to provide the actual name of the resource that this container represents.
     *
     * @type {String}
     * @readonly
     */
    get resourceName() {
      return this.name;
    }

    /**
     *
     * This getter returns the extension resource name which may be different from the base resource
     * name. For example, the extension resource name for data-description-overlay is data-description.
     *
     * @returns {String}
     */
    get extensionResourceName() {
      return this.resourceName;
    }

    /**
     * this base implementation should not be called; make it obvious if it happens during development
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get fullName() {
      throw undefinedPropertyError('fullName');
    }

    /**
     * The scope resolver is an object to retrieve scope instances given their names.
     * This is used to resolve the scope name of expressions like scopeName:typeName.
     * When defined, possible scope names are: "this", "page", "flow" or "application".
     * Cannot be in the defineProperties above because getScopeResolverMap() uses this.parent
     * @type {ScopeResolver}
     */
    get scopeResolver() {
      // Uses the lazy value pattern. The scope resolver is initialized only the first time
      // it is accessed then the property becomes read-only with the finalValue.

      // retrieve the scope resolver map for the current extension (and its dependencies)
      const extScopeResolverMap = ExtensionViewModel.getExtensionScopeResolverMap(this) || {};

      // Merge the 'this' property with the scope resolver map
      const finalValue = new ScopeResolver(
        Object.assign({ [Constants.THIS_PREFIX]: this }, this.getScopeResolverMap(), extScopeResolverMap),
      );
      // Once the final value is calculated, redefine the property as a value, so the
      // next time it is read, the value will be returned immediately.
      Object.defineProperty(this, 'scopeResolver', {
        value: finalValue,
        configurable: true,
      });

      return finalValue;
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor
     *
     * @type {String} the descriptor loader function name
     */
    static get descriptorLoaderName() {
      throw undefinedPropertyError('descriptorLoaderName');
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor. Supported only on specific
     * containers - flow, page and fragment
     *
     * @type {String} the descriptor loader function name
     */
    static get rulesDescriptorLoaderName() {
      throw undefinedPropertyError('rulesDescriptorLoaderName');
    }

    /**
     * The name of the runtime environment function to be used to load the module functions
     *
     * @type {String} the module loader function name
     */
    static get functionsLoaderName() {
      throw undefinedPropertyError('functionsLoaderName');
    }

    /**
     * The name of the runtime environment function to be used to load the html
     *
     * @type {String} the template loader function name
     */
    static get templateLoaderName() {
      throw undefinedPropertyError('templateLoaderName');
    }

    /**
     * Load the descriptor using the loader in the RuntimeEnvironment
     *
     * @return {Object} the descriptor object
     */
    descriptorLoader(resourceLocator) {
      return this.application.runtimeEnvironment[
        Object.getPrototypeOf(this).constructor.descriptorLoaderName](resourceLocator);
    }

    /**
     * Load the module functions using the loader in the RuntimeEnvironment
     *
     * @return {Object} the module object
     */
    functionsLoader(resourceLocator) {
      return this.application.runtimeEnvironment[
        Object.getPrototypeOf(this).constructor.functionsLoaderName](resourceLocator);
    }

    /**
     * Load the rules descriptor using the loader in the RuntimeEnvironment
     *
     * @return {Object} the rules descriptor object
     */
    rulesDescriptorLoader(resourceLocator) {
      return this.application.runtimeEnvironment[
        Object.getPrototypeOf(this).constructor.rulesDescriptorLoaderName](resourceLocator);
    }

    /**
     * Load the html content using the loader in the RuntimeEnvironment
     *
     * @return {Object} the module object
     */
    templateLoader(resourceLocator) {
      return this.application.runtimeEnvironment[
        Object.getPrototypeOf(this).constructor.templateLoaderName](resourceLocator);
    }

    /**
     * Determines whether this container extends from a base container. Used by events e.g., overridden by
     * containerExtension.
     * @returns {boolean}
     */
    // eslint-disable-next-line class-methods-use-this
    isExtension() {
      return false;
    }

    /**
     * @returns {boolean} true if the current container is an extension appUi (versus web app)
     */
    isExtensionAppUi() {
      // some tests don't seem to have extensionRegistry set
      return this.application.extensionRegistry && this.application.extensionRegistry.hasRegistry;
    }

    /**
     * Return the require path to the resource.
     * @return {String}
     */
    getResourceFolder() {
      return `${this.baseUrl}${this.resourceLoc}`;
    }

    /**
     * Return the require path with the name of the resource
     * @return {String}
     */
    getResourcePath() {
      return `${this.getResourceFolder()}${this.resourceName}`;
    }

    /**
     * Returns a scope resolver map where keys are scope name ("page", "flow" or "application")
     * and value the matching objects. This is used to build the scopeResolver object.
     *
     * @return {Object<String, Object>} an object which properties are scope
     * @package
     */
    // eslint-disable-next-line class-methods-use-this
    getScopeResolverMap() {
      throw undefinedMethodError('getScopeResolverMap');
    }

    /**
     * returns a Services object specific to this flow
     * @return {Services}
     */
    getServices() {
      // if services were created for this container return those
      // @ts-expect-error services is defined by containers that own service models.
      if (this.services) {
        // @ts-expect-error services is defined by containers that own service models.
        return this.services;
      }

      // return app services
      // check that this is not the application to avoid the loop
      // @ts-expect-error Application implements getServices().
      return (this !== this.application) && this.application.getServices();
    }

    /**
     * returns a list of Services, in priority order, high to low
     * Containers may override to provide different access to containers services, if needed
     *
     * We call application.getAllServices to include extensions
     * @return {Array<Services>}
     */
    getAllServices() {
      return Utils.toFlatUniqueArray(this.getServices(), this.application.getAllServices());
    }

    /**
     * Calling context of a container
     * @typedef {Object} CallingContext
     * @property {Extension} sourceExtension Extension that is used to resolve endpoints referenced by a container
     * @property {Extension} [parentExtension] Extension that directly referenced this container
     * @property {Extension} [owningExtension] Extension that owns this container definition
     */

    /**
     * Returns object containing the calling context of the container.
     *
     * @return {CallingContext}
     */
    getCallingContext() {
      return {
        sourceExtension: this.extension,
      };
    }

    /**
     * Called by the router in response to the enter callback
     * Implemented by Page and Flow
     *
     * @return {Promise}
     */
    // eslint-disable-next-line class-methods-use-this
    enter() {
      throw undefinedMethodError('enter');
    }

    /**
     * Called by the router in response to the exit callback
     * Implemented by Page and Flow
     *
     * @return {Promise}
     */
    // eslint-disable-next-line class-methods-use-this
    exit() {
      throw undefinedMethodError('exit');
    }

    /**
     * Create a child router of the parent container router.
     * Uses the id for the router name and for the parent state id. This will
     * allow the traversal of the router hierarchy to retrieve the leaf page.
     *
     * @return {VbRouter} the router for this container
     */
    createRouter(parent) {
      if (!parent.router) {
        return this.createRouter(parent.parent);
      }

      const childRouter = parent.router.getChildRouter(this.urlId);
      if (childRouter) {
        return childRouter;
      }

      return parent.router.createChildRouter(this.urlId, this.urlId);
    }

    initRouter() {
      if (this.router) {
        return;
      }
      const router = this.createRouter(this.parent);
      if (router) {
        // Make the router property readonly
        Object.defineProperty(this, 'router', {
          value: router,
          enumerable: true,
          configurable: true,
        });
        // Configure the router using a "state from id" callback
        this.router.configure(this.getRouterConfigureCallBack());
      }
    }

    /**
     * @param  {String} id the id of the flow or App UI to create
     * @param  {Container} container the parent container creating the flow
     * @return {Flow} the flow/App UI instance
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    createFlow(id, container) {
      throw undefinedMethodError('createFlow');
    }

    /**
     * The enter callback for VbRouter state transition used the
     * getRouterConfigureCallBack below
     * @param  {String} stateId
     * @return {Promise}
     */
    routerStateEnterCallback(stateId) {
      return Promise.resolve().then(() => {
        // Call getContainer instead of loadContainer because the container has
        // been created in canEnter.
        const cont = this.getContainer(stateId);

        return cont && cont.enter();
      });
    }

    /**
     * The canExit callback for VbRouter state transition used by the
     * getRouterConfigureCallBack below
     *
     * @param  {String}  containerId  The container identifier
     * @param  {String}  origin
     * @param  {Object}  details Additional info when origin is 'popState'
     * @return {Promise<boolean>} A promise that resolve to true if navigation can continue
     */
    routerStateCanExitCallback(containerId, origin, details) {
      return Promise.resolve().then(() => {
        const cont = this.getContainer(containerId);

        // If the container cannot be found, then return true to not
        // abort the navigation.
        return (cont ? cont.canExit(origin, details) : true);
      });
    }

    /**
     * The exit callback for VbRouter state transition used by the
     * getRouterConfigureCallBack below
     *
     * @param  {String} stateId
     * @return {Promise}
     */
    routerStateExitCallback(stateId) {
      return Promise.resolve().then(() => {
        const cont = this.getContainer(stateId);

        return cont && cont.exit();
      });
    }

    /**
     * Returns the callback to be used to configure the VbRouter of this container.
     * The callback provides a way for each router state to handle their state transition (enter/exit)
     * See http://jet.us.oracle.com/jsdocs/oj.Router.html#configure
     * @return {(id:string) => Object} the "state from id" callback
     */
    getRouterConfigureCallBack() {
      return (stateId) => {
        let state;

        if (stateId) {
          state = {
            // This is the canEnter called during router transition. It is used to
            // attempt to load the page.
            // canEnter expect a Promise that resolves as true to continue or false to veto
            // the navigation.
            canEnter: () => this.loadContainer(stateId).then((nested) => !!nested),
            // canExit expect a Promise that resolves as true to continue or false to veto
            // the navigation.
            canExit: (origin, details) => this.routerStateCanExitCallback(stateId, origin, details),
            // A way to call the page or flow when the router enter this state.
            enter: () => this.routerStateEnterCallback(stateId),
            // A way to call the page of the flow when the router exit this state.
            // This is earlier than the ojModule handleDeactivated.
            exit: () => this.routerStateExitCallback(stateId),
            value: this,
          };
        }

        return state;
      };
    }

    /**
     * Find a parent container with a matching id
     * @param {string} id
     * @return {Container | null} the parent container with the matching id or null if it cannot be found
     */
    findParentWithId(id) {
      if (this.id === id) {
        return this;
      }

      if (!this.parent) {
        return null;
      }

      return this.parent.findParentWithId(id);
    }

    /**
     * Return the first flow up in the parent hierarchy.
     * For flow, it's this.parent.parent, for page it's this.parent for
     * application it's null.
     *
     * @return {Flow} the first flow in the parent hierarchy
     */
    // eslint-disable-next-line class-methods-use-this
    getParentFlow() {
      throw undefinedMethodError('getParentFlow');
    }

    /**
     * Return true if this container should be hidden from the Url
     * Always false by default. Subclass can overwrite this behavior
     *
     * @return {boolean} true if it should be hidden from the Url
     */
    // eslint-disable-next-line class-methods-use-this
    hideFromUrl() {
      return false;
    }

    /**
     * Load the nested resource
     *
     * @param  {String} id the id of the flow
     * @param  {NavigationContext} [navContext] the context of the current navigation chain
     * @return {Promise<Container>} a promise that resolve to a Flow instance
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    loadContainer(id, navContext) {
      throw undefinedMethodError('loadContainer');
    }

    /**
     * Retrieve the cached instance of the nested container.
     * @param  {String} id the id of the page to retrieve
     * @return {Container} the page instance
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    getContainer(id) {
      throw undefinedMethodError('getContainer');
    }

    /**
     * The containership path relative to the application
     * Something like shellPageId/flowId/pageId.
     *
     * @return {String} the path
     */
    getContainerPath() {
      if (this.parent) {
        const parentId = this.parent.getContainerPath();
        return parentId ? `${parentId}${Constants.PATH_SEPARATOR}${this.id}` : this.id;
      }

      return '';
    }

    /**
     * Calculate the path to use to navigate to this page using the router.
     * This is different than getContainerPath because this one take into account the
     * hidden flow/page, so instead of the path being shellPage/flow/page, the path
     * will just be flow/page.
     *
     * @return {String}
     */
    calculateNavPath() {
      if (this.parent) {
        const parentPath = this.parent.getNavPath();
        if (this.hideFromUrl()) {
          return parentPath;
        }
        return parentPath ? `${parentPath}/${this.urlId}` : this.urlId;
      }

      return '';
    }

    /**
     * @return {String} the navigation path
     */
    getNavPath() {
      if (this.navPath === null) {
        this.navPath = this.calculateNavPath();
      }

      return this.navPath;
    }

    // eslint-disable-next-line class-methods-use-this
    getViewModel() {
      throw undefinedMethodError('getViewModel');
    }

    /**
     * Generate a unique name to be used for the scope.
     * The name is unique and has the name of the class and the path to make it easier to
     * find the scope owner when debugging.
     *
     * @return {String} a new scope name
     */
    getNewScopeName() {
      return `${this.className}/${this.fullPath}`;
    }

    /**
     * Loads the metadata of a container (.json file) and populate/initialize the definition class property
     *
     * @return {Promise}
     */
    loadMetadata() {
      this.loadMetadataPromise = this.loadMetadataPromise || this.descriptorLoader(this.getResourcePath())
        .then((definition) => {
          // send the descriptor to the debugger
          ApplicationDebugStream.descriptorLoaded(this.getResourcePath(), definition, this);

          // Initialize the context object, for expressions ($page OR $flow OR $chain, etc)
          this.expressionContext = new (Object.getPrototypeOf(this).constructor.ContextType)(this);

          // Initialize all the default values of the descriptor
          this.initDefault(definition);

          return this.definition;
        });

      return this.loadMetadataPromise;
    }

    /**
     * This method returns a promise to load the container definition. It uses the runtime environment
     * and the resource locator provided by each subclass the load the appropriate parts.
     * @return {Promise.<Object>} a promise that resolves when the file is loaded with the page definition object,
     * and any other dependencies (translations, imports).
     */
    loadDescriptor() {
      this.loadDescriptorPromise = this.loadDescriptorPromise || this.loadMetadata()
        // First check if we can enter the page, no point loading components or translation bundle
        // if the page cannot be accessed by the user.
        .then(() => this.checkAccess())
        // now, load anything that the JSON model requires, in parallel, and wait for all of the promises.
        // Import any JET components declared in imports.components, and resolve with the descriptor
        // and also, in parallel, load translation bundles and the extensions
        .then(() => this.loadContainerDependencies())
        // Merge the content on top of the base object
        .then(() => {
          this.combineExtensions();
          return this.definition;
        });

      return this.loadDescriptorPromise;
    }

    /**
     * This method loads anything that the JSON model requires, in parallel, and waits for all of the promises.
     * It imports any JET components declared in imports.components, and loads translation bundles and the extensions.
     *
     * @returns {Promise} a promise that resolves when all dependencies (translations, imports...) are loaded.
     */
    loadContainerDependencies() {
      const registry = this.application.extensionRegistry;
      const descriptorManifestGeneration = registry.manifestGeneration;

      const handleExtensionLoadError = (err) => {
        // Swallow the error so that it doesn't break the application, but no extension will be available
        this.extensionsArray = [];
        this.extensions = {};
        this.log.error('Error loading extensions', err);
      };

      const loadExtensionPromise = this.loadExtensions()
        .then(() => ExtensionViewModel.loadExtensionDescriptor(this))
        .catch(handleExtensionLoadError);

      return Promise.all([loadExtensionPromise, this.loadImports(), this.loadTranslationBundles(), this.loadRules()])
        .then(() => registry.whenManifestStable())
        .then(() => {
          if (descriptorManifestGeneration === registry.manifestGeneration) {
            return undefined;
          }

          // Full-manifest recovery refreshes Extension objects in place and clears extensionResources. If recovery
          // started while container dependencies were loading, reload descriptors before scope resolvers create
          // ExtensionScope instances from extensionResources.definition.
          return ExtensionViewModel.loadExtensionDescriptor(this)
            .catch(handleExtensionLoadError);
        });
    }

    /**
     * Return the promise to load the functions module using the name of a functions loader.
     * @return {Promise.<Function|Object>}  the promise of a constructor a singleton object
     */
    loadFunctionModule() {
      if (this.loadFunctionsPromise) {
        return this.loadFunctionsPromise;
      }

      const promises = [];
      // Keep a reference of the loading promise so that multiple function can wait
      // on the same promise to be resolved.
      const loadFunctionsPromise = this.functionsLoader(this.getResourcePath())
        .then((FunctionModule) => {
          this.functions = ModelUtils.initializeJsModule(FunctionModule, this);
        })
        .catch((e) => {
          const moduleName = `${this.getResourceFolder()}${this.fullName}${this.isExtension() ? '-x' : ''}.js`;
          // swallow errors for missing files, log the rest
          if (HttpError.isFileNotFound(e)) {
            this.log.warn('Module', moduleName, 'does not exist.');
          } else if (!e.requireType || e.requireType !== 'scripterror') {
            this.log.error('Error loading module', moduleName, e);
          } else if (e.requireType === 'scripterror' && e.requireModules) {
            // Logging as a warning since $functions may not always be used in the action chain.
            this.log.warn(`$functions could be null; failed to load modules: ${e.requireModules.join(', ')}.`);
          }
        });

      promises.push(loadFunctionsPromise);
      // Propagate the loadFunctionModule call to all extensions
      promises.push(...this.traverseExtensions('loadFunctionModule'));
      this.loadFunctionsPromise = Promise.all(promises);

      return this.loadFunctionsPromise;
    }

    /**
     * The class to use to create layout mode.
     * appPackage has a different one
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get layoutModelClass() {
      return 'vb/private/stateManagement/layout';
    }

    /**
     * Loads rules if the definition includes interface constants whose value need to come from rules.
     * @returns {Promise} resolves when the rules engine has been created and primed with the rules model
     */
    loadRules() {
      if (!this.loadRulesPromise) {
        const rulesPath = `${this.resourceLoc}${this.fullName}${this.isExtension() ? '-rules-x' : '-rules'}.json`;
        // have two possibilities -
        // each extension (base or its customer) may have rules or not
        // if base extension has no rules, we won't create the rules engine just yet. If its customer extension does
        // have rules, then that creates the rules engine on the base
        if (this.supportsRules()) {
          // we always attempt to load the file, as rules are explicitly opted in. It better exist!
          this.loadRulesPromise = this.rulesDescriptorLoader(this.getResourcePath())
            .then((rulesDef) => {
              // validates constants before priming rules engine
              const primeRulesEngine = (constantsDef) => {
                const rulesConstantsDefs = this.getRuleConstants(constantsDef);
                this.getRulesEngine().primeWithRules(this, rulesConstantsDefs, rulesDef);
              };

              let cdefs;
              if (this.isExtensionAppUi() && this.isExtension()) {
                cdefs = (this.definition.extensions && this.definition.extensions.constants) || {};
              } else {
                // for base extension or regular web app
                cdefs = this.definition.interface.constants || {};
              }

              primeRulesEngine(cdefs);
            })
            .catch((e) => {
              // it's not required for a rules file to always exist. So log a warning for missing files, log the rest
              if (HttpError.isFileNotFound(e)) {
                const msg = `The rules file ${rulesPath} does not exist. Please check your configuration!`;
                this.log.error(msg, e);
              } else {
                this.log.error('Encountered error when loading and parsing the rules file', rulesPath, e);
              }
            });
        } else {
          this.loadRulesPromise = Promise.resolve();
        }
      }
      return this.loadRulesPromise;
    }

    /**
     * returns a filtered Object of constants, but first check if all interface constants are correctly configured
     * @param constants
     * @returns {Object} map of all constants. It will be empty for extensions/constants and only filled for base defs
     */
    getRuleConstants(constants) {
      // when rules are defined aside from the below cases, everything else is an error
      // - extensions no longer can have definitions under extensions/constants if they participate in rules.
      // - base can have definitions for constants except the defaultValue property
      const rulesConstants = Object.entries(constants).filter(([key, cDef]) => {
        const valid = !!(cDef && cDef[Constants.VariableProperties.DEFAULTVALUE] === undefined);
        if (!valid) {
          this.log.error('A rules file exists for the container', this.id, 'but the constant', key,
            'has a defaultValue property configured which is disallowed. It is required that values for all interface',
            'constants come, either from the rules configuration or from the container definition, but not both.');
        }

        return valid;
      });

      return Object.fromEntries(rulesConstants);
    }

    /**
     * Generally there can be only one rules engine per (base) container. If there is none create one and return it.
     * Every container always creates the rules engine whether it uses rules to evaluate values for its constants or
     * not. This is because rules can be added by a customer extension on its extended constants even when the
     * factory doesn't use it.
     * @returns {RulesEngine} instance of RulesEngine or undefined if no rulesEngine is required
     */
    getRulesEngine(createNew = true) {
      const container = this.isExtension() ? this.base : this;
      if (createNew && !container.rulesEngine && this.supportsRules()) {
        container.rulesEngine = new RulesEngine(container, this.extension.dependencies);
      }
      return container.rulesEngine;
    }

    /**
     * The options property needs to be set && the container needs to enable rules as some containers may not enable
     * rules support, like application or layout for example
     * @param {boolean} anyLevel when true checks on all its extensions for rules support. otherwise checks the
     * current container
     * @returns {boolean}
     */
    supportsRules(anyLevel = false) {
      if (anyLevel) {
        const root = this.isExtension() ? this.base : this;
        // check the base and all its extensions
        const allExts = [root].concat(root.extensionsArray);
        return !!allExts.find((ext) => checkRulesSupport(ext));
      }

      return checkRulesSupport(this);
    }

    /**
     * For app, flows, etc, the name of the chain folder is chains.
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get chainsFolderName() {
      return 'chains';
    }

    /**
     * Preload chains referenced by vbNotification, vbResourceChanged, and vbNewContentAvailable event listeners.
     *
     * NOTE: Since preloadChains calls loadChain which assumes this.chains is already initialized
     * by initializeActionChains, this method should only be called after initializeActionChains
     * has been called on the container.
     *
     * @returns {Promise}
     */
    preloadChains() {
      return Promise.resolve().then(() => {
        const { eventListeners } = this.definition;
        const promises = [];

        Object.keys(eventListeners).forEach((eventType) => {
          if (eventType === Constants.RESOURCE_CHANGED_EVENT
            || eventType === Constants.NOTIFICATION_EVENT
            || eventType === Constants.NEW_CONTENT_AVAILABLE) {
            const eventListener = eventListeners[eventType];
            const { chains } = eventListener;

            if (chains) {
              chains.forEach((chain) => {
                // to keep things simple and conservative, we will not load chains that are
                // scoped, e.g., application:myChain and page:myChain
                const chainId = chain.chain || chain.chainId;

                if (chainId.indexOf(':') === -1) {
                  promises.push(this.loadChain(chainId, ActionChainRunner.create(chain), true));
                }
              });
            }
          }
        });

        return Promise.allSettled(promises);
      });
    }

    /**
     * Dynamically load a file-based action chain identified by the given chainId.
     *
     * @param {String} chainId the id for the chain load
     * @param chainRunner used to load the source file for the chain
     * @param skipLoadQueue if true, skip the load queue
     * @returns {Promise}
     */
    loadChain(chainId, chainRunner, skipLoadQueue) {
      const callback = () => Promise.resolve()
        .then(() => {
          // If the chain is already defined in the container descriptor, it will take precedence over
          // file-based chain.
          const chain = this.chains[chainId];
          if (chain !== undefined) {
            return chain;
          }

          const chainPath = `${this.getResourceFolder()}${this.chainsFolderName}/${chainId}`;

          // load the source file for the chain
          return chainRunner.loadChainSource(chainPath).then((source) => {
            this.chains[chainId] = source;

            // need to resend the descriptor to the debugger to update the newly loaded chain
            ApplicationDebugStream.descriptorLoaded(this.getResourcePath(), this.definition, this);

            return this.chains[chainId];
          })
            .catch((err) => {
              if (chainRunner.shouldTriggerResourceChangedEvent()) {
                this.triggerResourceChangedEvent(chainPath);
              }

              logger.error('Failed to load action chain', chainId, err);

              // set the chain to null so we don't try to load it again.
              this.chains[chainId] = null;

              return null;
            });
        });

      return skipLoadQueue ? callback() : this.queueLoadChain(callback);
    }

    /**
     * For JS chain, since requirejs loads JS code by inserting a script tag into the DOM,
     * there is no fetch request being made that the ResourceChangedPlugin can intercept to
     * check for the x-vb-changed-header response header in order to trigger the vbResourceChanged
     * event. To work around this, we make an explicit fetch call out-of-band to trigger the
     * vbResourceChanged event. Note that this should not introduce any performance override
     * because it's highly unlikely for there to be a missing JS chain unless the resources
     * have been re-deployed.
     *
     * @param chainPath relative path for the chain
     */
    triggerResourceChangedEvent(chainPath) {
      // don't send the fetch request for V2 applications since vbResourceChanged event is not
      // supported
      if (Utils.isHostApplication()) {
        return;
      }

      // convert the relative path to an absolute path
      const absChainUrl = `${this.absoluteUrl}${chainPath}.js`;

      fetch(absChainUrl, {
        headers: {
          'Cache-Control': 'no-cache, no-store', // bypass browser cache
        },
      }).catch(() => {
        // ignore
      });
    }

    /**
     * Queue the loading of action chains to work around issues that may arise from lazy
     * loading of file-based action chains.
     *
     * @param callback callback for loading the action chain
     * @returns {Promise}
     */
    queueLoadChain(callback) {
      this.loadChainPromise = this.loadChainPromise || Promise.resolve();

      // chain the callback to load the action chain
      this.loadChainPromise = this.loadChainPromise.then(callback);

      return this.loadChainPromise;
    }

    /**
     * Merge the content of the extension to the base object.
     * This is done on the definition of the base object before the constants are created.
     * @param extendedConstants
     * @param extension
     */
    combineExtensionConstants(extendedConstants, extension) {
      // This is the list of constants that are allowed to be modified by extensions.
      const { constants } = this.definition.interface;

      // Traverse all extended constants in the 'extension' and apply the new value to the base constant's definition.
      Object.keys(extendedConstants).forEach((constName) => {
        const constant = constants[constName];
        if (constant) {
          const extendedConstant = extendedConstants[constName];

          // track all rules extensions where an extended constant needs to execute its rules in the proper context
          constant.rulesScopes = constant.rulesScopes || [];

          // Override the value of the constant with the value from the extension if it's present
          if (typeof extendedConstant === 'object') {
            const getsValueFromRules = isValueFromRules(extendedConstant);
            if (extendedConstant.defaultValue !== undefined && getsValueFromRules) {
              const errMsg = `Constant ${constName} in ${this.name} can either derive its value from rules or can`
                + ' have defaultValue configured!';
              throw new Error(errMsg);
            }

            if (extendedConstant.defaultValue !== undefined) {
              constant.defaultValue = extendedConstant.defaultValue;
            } else if (getsValueFromRules) {
              constant.rulesScopes.unshift({
                [Constants.VariablePropertiesInternal.VALUEFROM]: Constants.VariablePropertyValueFrom.RULES,
                extension,
              });
              return;
            } else {
              return;
            }
          } else {
            constant.defaultValue = extendedConstant;
          }

          // Add a reference to the extension extending the constant with configured defaultValue. This will be
          // used to retrieve the proper scope in order to evaluate the expression
          constant.extension = extension;
          constant.rulesScopes.unshift({
            [Constants.VariablePropertiesInternal.VALUEFROM]: Constants.VariablePropertyValueFrom.INLINE,
            hasDefaultValue: (constant.defaultValue !== undefined),
            extension,
          });
        } else {
          this.log.warn('Constant', constName, 'is not part of the interface of', this.className, this.name);
        }
      });
    }

    /**
     * Merge the content of all extensions in the base.
     * At the moment only merging constants.
     */
    combineExtensions() {
      // Traverse all the extensions
      this.extensionsArray.forEach((extension) => {
        const extensionDef = extension.definition;
        if (extensionDef) {
          // This points to the extensions' definition where extensions is already initialized in initDefault().
          // With rules, extension.constants may be entirely absent because their values come from rules. So use the
          // constantsDefs setup by rules engine. Example, extension constants whose value comes from
          // rules will be internally configured as -  "foo": { valueFrom: 'rules' }
          let extendedConstants = extensionDef.extensions.constants;
          if (!extendedConstants || Object.keys(extendedConstants).length === 0) {
            // if the extension supports rules grab the constants built from overlay
            extendedConstants = extension.supportsRules()
              ? this.getRulesEngine().getConstantsDef(extension.extensionId) : {};
          }

          if (extendedConstants) {
            this.combineExtensionConstants(extendedConstants, extension);
          }
        }
      });

      // for all interface constants on base, also shove the current extension to rulesScopes for convenience
      const { constants } = this.definition.interface;
      Object.values(constants).forEach((c) => {
        const constant = c;
        let context;
        if (isValueFromRules(constant)) {
          context = {
            [Constants.VariablePropertiesInternal.VALUEFROM]: Constants.VariablePropertyValueFrom.RULES,
            extension: this,
          };
        } else {
          context = {
            [Constants.VariablePropertiesInternal.VALUEFROM]: Constants.VariablePropertyValueFrom.INLINE,
            hasDefaultValue: constant.defaultValue !== undefined,
            extension: this,
          };
        }
        constant.rulesScopes = (constant.rulesScopes || []);
        constant.rulesScopes.push(context);
      });
    }

    /**
     * Invoke the function func on each of the extensions
     * @param  {string} func the name of the function to invoke
     * @param {*} args optional arguments, passed directly to the function.
     * @return {Array}       an array where each element is the result of the function called
     */
    traverseExtensions(func, ...args) {
      return this.extensionsArray.map((extension) => extension[func](...args));
    }

    /**
     * The export property is an object with the variables, constants and events that are
     * exported by this container. It corresponds to the object declared in the interface.
     * section of the json file.
     * See baseContext.js for usage.
     * @type {Object}
     */
    get export() {
      if (!this.exported) {
        const exported = {
          enums: {},
          constants: {},
          variables: {},
        };

        // enums need to be populated first, so that constant's values are properly evaluated next
        // when values reference $base.enums
        const { types } = this.definition.interface;

        if (types) {
          Object.keys(types).forEach((name) => {
            const enumDef = this.enums[name];
            if (enumDef) {
              exported.enums[name] = enumDef;
            }
          });
          // Protects export.enums from being modified. Enums cannot be added, removed or modified.
          Object.freeze(exported.enums);
        }

        const { constants, variables: variableDefs } = this.definition.interface;

        if (constants) {
          // if rules are supported anywhere in the extensions hierarchy, setup a special getter
          if (!this.supportsRules(true)) {
            Object.keys(constants).forEach((name) => {
              exported.constants[name] = this.scope.variableNamespaces[Constants.VariableNamespace.CONSTANTS][name];
            });
          } else {
            const vars = this.scope.variableNamespaces[Constants.VariableNamespace.CONSTANTS];
            Object.keys(constants).forEach((name) => {
              const descriptor = {
                get() {
                  return vars[name];
                },
                configurable: false,
                enumerable: true,
              };
              Object.defineProperty(exported.constants, name, descriptor);
            });
          }

          // Map the exported constants namespace to its scope so $withEnv can evaluate it with extra $env values.
          WithEnvContext.registerNamespace(this.scope, exported.constants, Constants.VariableNamespace.CONSTANTS);
          // Protects export.constants from being modified. Constants cannot be added, removed or modified.
          Object.freeze(exported.constants);
        }

        // Only add getter to variables defined in the interface section
        if (variableDefs) {
          const vars = this.scope.variableNamespaces[Constants.VariableNamespace.VARIABLES];
          Object.keys(variableDefs).forEach((name) => {
            const descriptor = {
              get() {
                return vars[name];
              },
              enumerable: true,
            };

            // If the variable is writable, defines a setter. The absence of setter makes it read-only.
            if (variableDefs[name].mode === 'readWrite') {
              descriptor.set = (value) => {
                vars[name] = value;
              };
            }

            Object.defineProperty(exported.variables, name, descriptor);
          });
          // Protects export.variables from being modified. Variables cannot be added, removed or modified.
          Object.preventExtensions(exported.variables);
        }

        // Unclear why this is the case but this.exported needs to be assigned at the very end of all
        // defineProperties have been set.
        this.exported = exported;
      }

      return this.exported;
    }

    /**
     * Loads all the extensions that apply to this container. It resolves once the extensions are loaded
     * and stored in the base container.
     * @return {Promise} a promise resolving when each extension for this container is loaded and stored
     */
    async loadExtensions() {
      // TODO: simplify, only one param?
      const exts = await this.application.extensionRegistry
        .loadContainerExtensions(this.path, /** @type {*} */ (this));
      // A partial digest update exposes the cumulative registry. Do not create another container for an
      // extension that is already applied to this container when a newly available App UI triggers a reload.
      const extensionIds = new Set(Object.keys(this.extensions));
      const newExtensions = exts.filter((extension) => {
        if (extensionIds.has(extension.extensionId)) {
          return false;
        }
        extensionIds.add(extension.extensionId);
        return true;
      });
      this.storeExtensions(newExtensions);
    }

    /**
     * storeExtensions
     */
    storeExtensions(extensionsArray) {
      if (extensionsArray.length > 0) {
        this.extensionsArray.push(...extensionsArray);

        extensionsArray.forEach((extension) => {
          this.extensions[extension.extensionId] = extension;
        });
      }
    }

    /**
     * Initialize the descriptor object default value
     */
    initDefault(definition) {
      const def = definition;

      def.types = def.types || {};
      def.variables = def.variables || {};
      def.metadata = def.metadata || {};
      def.constants = def.constants || {};
      def.events = def.events || {};
      def.eventListeners = def.eventListeners || {};
      def.interface = def.interface || {};
      def.interface.events = def.interface.events || {};
      def.interface.constants = def.interface.constants || {};
      def.interface.variables = def.interface.variables || {};
      def.interface.types = def.interface.types || {};
      def.webSockets = def.webSockets || {};
      def.aiResponseEventListeners = def.aiResponseEventListeners || {};
      def.aiRequestEventListeners = def.aiRequestEventListeners || {};

      // Initialize the security entry with non-empty values
      def.security = def.security || {};
      const { security } = def;
      if (!security.access) {
        security.access = {};
      }

      // filter decorators
      Utils.removeDecorators(def.constants);
      Utils.removeDecorators(def.interface.constants);
      Utils.removeDecorators(def.variables);
      Utils.removeDecorators(def.interface.variables);
      Utils.removeDecorators(def.eventListeners);
      Utils.removeDecorators(def.types);
      Utils.removeDecorators(def.interface.types);
      Utils.removeDecorators(def.chains);

      // Make definition a readonly properties for safety
      Object.defineProperty(this, 'definition', {
        value: def,
        configurable: true,
        enumerable: true,
      });

      // Start the initialization of the translation bundles model, but don't block on it
      this.loadTranslationBundles();
    }

    /**
     * Save the parameters to the runtimeEnvironment
     * This is used by the DT to pass parameters between pages shown in different iframes
     * @param  {Object} params
     */
    saveNavigateParamsInDt(params) {
      if (params) {
        this.application.runtimeEnvironment.saveInputParameters({
          get plainParams() {
            // make sure we clone the parameters to remove getters and setters so it's a plain object
            return Utils.cloneObject(params);
          },
        });
      }
    }

    /**
     * Prepares the arguments and calls canNavigateToPage. This is only needed for DT to
     * be able cancel navigation.
     * @param  {Object} options the object with all the navigateToPage option
     * @return {Promise<Boolean>} a promise resolving to true when navigation is allowed
     */
    validateNavigationInDt(options) {
      const rtEnv = this.application.runtimeEnvironment;
      // For DT environment, retrieve current and destination page then invoke the
      // canNavigatePage to give a chance to DT to cancel navigation.
      const currentPath = this.application.getCurrentPagePath();

      if (options.operation === Constants.NavigateOperation.PAGE
        || options.operation === Constants.NavigateOperation.PAGE_OLD) {
        let destinationPath = options.page || '';

        // Build the destination full path
        if (destinationPath[0] !== '/' && this.parent) {
          const parentPath = this.parent.getContainerPath();
          if (parentPath) {
            destinationPath = `${parentPath}/${destinationPath}`;
          }
        }

        this.saveNavigateParamsInDt(options.params);
        return rtEnv.canNavigateToPage(currentPath, destinationPath);
      }

      this.saveNavigateParamsInDt(options.params);
      return rtEnv.canNavigate(currentPath, options);
    }

    /**
     * returns an error message when the options are incorrect.
     * @param options
     */
    validateNavigation(options) {
      // When navigating to an other App UI, verify the destination is marked as navigable
      if (options.operation === Constants.NavigateOperation.APP_UI
        && options.application !== '' && options.application !== (this.package && this.package.id)) {
        this.application.appUiInfos.validateNavigation(options);
      }
    }

    /**
     * Navigate to a page of the application with given input parameters.
     *
     * @param {Object} options         The navigation options
     * @param {String} options.operation The navigation operation to perform
     * @param {String} options.application The id of the destination App UI
     * @param {String} options.page    The destination page path relative to this flow (required)
     * @param {String} options.flow    The id of the destination flow.
     * @param {String} options.target  An option destination when using the flow property
     * @param {Object} options.params  A map of URL parameters (optional)
     * @param {String} options.history Effect on the browser history. Allowed value are 'replace', 'skip' or 'push'.
     *                                 If the value is 'replace', the current browser history entry is replace,
     *                                 meaning that back button will not go back to it.
     *                                 If the value is 'skip', the URL is left untouched.
     *                                 (optional and default is push)
     * @param {NavigationContext} navContext the navigation context
     * @returns {Promise<NavigateResult>}  a promise that resolves to an object with a property named
     * 'navigated' which value is true or false depending if the navigation reaches the target page
     */
    navigateOperation(options, navContext) {
      return Promise.resolve().then(() => {
        let getLeafPageInstancePromise;
        let destination;
        let message = '';
        let target;

        switch (options.operation) {
          case Constants.NavigateOperation.PAGE:
            destination = options.page;
            message = 'page';
            getLeafPageInstancePromise = this.getLeafPageInstance(destination, navContext);
            break;

          case Constants.NavigateOperation.FLOW:
            destination = options.flow;
            message = 'flow';
            // Changing the parent flow consist of change the flow of the parent page
            target = options.target === 'parent' ? this.parent.parent : this;
            getLeafPageInstancePromise = target.getLeafPageInstanceFromFlowId(destination, navContext);
            break;

          case Constants.NavigateOperation.APP_UI:
            {
              // When the application parameter is '', it navigates to the current App UI.
              const appUiId = options.application || this.package.id;
              destination = appUiId;
              message = 'App UI';
              getLeafPageInstancePromise = this.getLeafPageInstanceFromAppUiId(appUiId, navContext);
            }
            break;

          case Constants.NavigateOperation.PAGE_OLD:
            destination = options.page;
            message = 'path';
            getLeafPageInstancePromise = this.getLeafPageInstance(destination, navContext);
            break;

          default:
            break;
        }

        this.log.info('Starting navigation to', message, destination, 'with options:', options);

        const oldInputParams = Utils.cloneObject(this.history.getInputParameters());
        const newInputParams = options.params;

        // diffInputParam is defined if the input parameters are different from what's on the browser history.
        const diffInputParam = Utils.diff(newInputParams, oldInputParams);
        if (diffInputParam) {
          // Store a copy of the new input parameters to the history state
          this.history.setInputParameters(newInputParams);
        }

        return getLeafPageInstancePromise.then((page) => {
          // Page can be undefined when navigation is cancelled
          if (page) {
            return this.navigate(page, options, diffInputParam, oldInputParams);
          }

          // Clean up any created page during the cancelled/aborted navigation
          navContext.clean();

          return Router.DID_NOT_NAVIGATE;
        });
      });
    }

    /**
     * Gets the leaf page instance from App UI id.
     * Container running in the switcher have a different implementation.
     *
     * @param  {String}   appUiId the id of the App UI to navigate to
     * @param  {NavigationContext}   navContext including options with navigate action parameters
     * @return {Promise}  a Promise resolving to the leaf page instance
     */
    getLeafPageInstanceFromAppUiId(appUiId, navContext) {
      // By default, delegate to the application to get the leaf page
      return this.application.getLeafPageInstanceFromAppUiId(appUiId, navContext);
    }

    /**
     * Given a path, returns a promise that resolves with the page instance nested the deepest.
     * The page path can be a page id or a path of ids starting with a page id like:
     * pageId/flowId/pageId...
     * When done traversing the given path, continue drilling in the default page
     * or default container up to the point where the leaf page is reached which is
     * a page without a default flow.
     *
     * Called by navigateOperation
     *
     * @param  {String} pagePath   the navigation path
     * @param  {NavigationContext} navContext the navigation context
     * @returns {Promise}  a Promise resolving to the leaf page instance
     */
    getLeafPageInstance(pagePath, navContext) {
      // If the path starts with /, starts the navigation relative to
      // the default page of the application
      if (pagePath[0] === '/') {
        return this.application.getLeafPageInstance(pagePath.substr(1), navContext);
      }

      if (pagePath === '') {
        // With the navigate action, an empty path means navigate to the default page of the parent flow
        return this.parent.loadDefaultContainers(navContext);
      }

      // 3 phases:
      //   * Load the container for the first segment of the path
      //   * Load any other segment in the path
      //   * Load default containers

      const segments = pagePath.split('/');

      // Load the first segment
      return this.loadFirstPathSegment(segments.shift(), navContext)
        .then((nestedContainer) => {
          // It is possible firstSegment return undefined when beforeEnter return false or
          // when the page is not found.
          // In that case, the navigation is cancelled so no point drilling in nested page,
          // just return undefined.
          if (!nestedContainer) {
            return Promise.resolve();
          }

          // Loads other segments in the path
          return nestedContainer.loadPathSegments(segments, navContext);
        })
        .then((nestedContainer) => {
          if (!nestedContainer) {
            return Promise.resolve();
          }

          // Now that we loaded the container of the last segment of the path,
          // drill in the default to reach the leaf page.
          return nestedContainer.loadDefaultContainers(navContext);
        })
        .catch((error) => {
          if (HttpError.isFileNotFound(error)) {
            this.fireNotificationEvent(`${Utils.formatLoadError(error)} while loading path "${pagePath}".`);
          } else {
            throw error;
          }
        });
    }

    /**
     * Fire a notification event using a message.
     * The event uses the default setting: empty summary, persist display mode and error type
     *
     * @param  {String} message the message to put in the payload of the event
     */
    fireNotificationEvent(message) {
      const eventHelper = new EventHelper({ container: this });
      return eventHelper.fireNotificationEvent({ message });
    }

    /**
     * Retrieve the leaf page instance when navigating to a flow.
     * Given a flowId and the navigation action parameters stored in navContext.options.
     * and return a promise that resolves with the page instance nested the deepest.
     * When done traversing the given path, continue drilling in the default page
     * or default container up to the point where the leaf page is reached, which is
     * a page without a default flow.
     *
     * Called by navigateOperation
     *
     * @param  {String} flowId the id of the flow to navigate to
     * @param  {NavigationContext} navContext including options with navigate action parameters
     * @return {Promise}  a Promise resolving to the leaf page instance
     */
    getLeafPageInstanceFromFlowId(flowId, navContext) {
      let pagePath = navContext.options.page;

      // Ignore the slash at the beginning of the page path because when navigating from a flow,
      // the page location is always relative to the flow.
      if (pagePath && pagePath[0] === '/') {
        pagePath = pagePath.substring(1);
        // eslint-disable-next-line no-param-reassign
        navContext.options.page = pagePath;
      }

      // An empty flow id without a page means navigate to the default page or the default flow
      if (!flowId && !pagePath) {
        return this.loadDefaultContainers(navContext);
      }

      // Load the flow
      return this.loadContainer(flowId, navContext)
        .then((flow) => {
          if (flow) {
            if (!pagePath) {
              return flow.loadDefaultContainers(navContext);
            }

            return flow.getLeafPageInstance(pagePath, navContext);
          }

          return undefined;
        })
        .catch((error) => {
          if (HttpError.isFileNotFound(error)) {
            this.log.error(error);
            this.fireNotificationEvent(`${Utils.formatLoadError(error)} while loading "${flowId}".`);
          } else {
            throw error;
          }
        });
    }

    /**
     * Retrieve a flow instance. If it doesn't exist, create and load it but never returns
     * undefined.
     *
     * @param  {String} id the flow id
     * @param  {NavigationContext} [navContext] the context of the current navigation chain
     * @return {Promise<Container>} a promise which resolve with the flow instance
     */
    loadFlowFromId(id, navContext) {
      return Promise.resolve().then(() => {
        let flow = this.flows[id];

        if (flow) {
          return flow;
        }

        flow = this.createFlow(id, this);

        return flow.load()
          .then(() => flow.processDefaultPage())
          .then(() => {
            if (!navContext) {
              // A child Router was just created in the flow so if the load was triggered by the
              // router (which is the case navContext is not defined) then we need to call sync on
              // the JET router to synchronize the state of the routers with the URL.
              // This can happen in 2 cases:
              //   1) when the page is refreshed
              //   2) when going back or forward in the browser history.
              Router.sync();
            }

            this.flows[id] = flow;

            return flow;
          });
      });
    }

    /**
     * From the current container this method drills into the defaults to
     * get to the leaf page.
     * @param  {NavigationContext} navContext the context of the current navigation chain
     * @return {Promise<Container|undefined>} a promise that resolves to the leaf page or undefined
     */
    loadDefaultContainers(navContext) {
      const id = this.router.defaultStateId;
      if (id) {
        return this.loadContainer(id, navContext)
          .then((nestedContainer) => (nestedContainer
            ? nestedContainer.loadDefaultContainers(navContext)
            : undefined));
      }
      // If there is no default, we reached the leaf page
      return Promise.resolve(this);
    }

    /**
     * Loads the container for the first segment of a navigation path
     * The default is to load the container directly. The Page object has a
     * different implementation
     *
     * @param  {String} id
     * @param  {NavigationContext} navContext
     * @return {Promise}
     */
    loadFirstPathSegment(id, navContext) {
      return this.loadContainer(id, navContext);
    }

    /**
     * Traverse the path segment and load the containers
     * @param  {Array} segments array of path segments
     * @param  {NavigationContext} navContext
     * @return {Promise} a promise that resolves to a container or undefined
     */
    loadPathSegments(segments, navContext) {
      if (segments.length > 0) {
        // Loads the container recurse with what is left of the array.
        return this.loadContainer(segments.shift(), navContext)
          .then((nestedContainer) => (nestedContainer
            ? nestedContainer.loadPathSegments(segments, navContext)
            : undefined));
      }

      return Promise.resolve(this);
    }

    /**
     * Property rootRouter
     * The top level router in the hierarchy. It's the application VbRouter
     * Containers in a switcher implement this property differently
     *
     * @type {RootRouter}
     */
    // eslint-disable-next-line class-methods-use-this
    get rootRouter() {
      return Router.rootRouter;
    }

    /**
     * The history instance to use for this container.
     * When a switcher component is in the page, the value of VbRouter.rootInstance
     * depends on the current switcher element. Container in a Switcher have a different
     * implementation of the VbRootRouter and the History class
     *
     * @type {VbHistory}
     */
    // eslint-disable-next-line class-methods-use-this
    get history() {
      return VbRouter.rootInstance.history;
    }

    /**
     * Given a page object, navigate to this page
     * @param  {Page} page             the page object
     * @param  {Object} options        the navigateToPage action parameters
     * @param  {Object} diffInputParam an object truthy if old an new input parameters are different
     * @param  {Object} oldInputParams an object representing the old input parameter values
     * @return {NavigateResult}        an object with the boolean property navigated
     */
    navigate(page, options, diffInputParam, oldInputParams) {
      // Turn on the skip mode on the history so that URL is not modified
      this.history.setSkipMode(options.history === Constants.HistoryMode.SKIP);

      const arePageTheSame = this.rootRouter.getCurrentNavPath() === page.getNavPath();
      if (arePageTheSame) {
        // If input params are the same as what is on the browser history,
        // there is no state change so nothing to do
        const navigated = diffInputParam
          ? this.navigateToSamePage(page, options, oldInputParams)
          : Router.DID_NOT_NAVIGATE;

        return navigated;
      }

      return this.navigateToOtherPage(page, options, oldInputParams);
    }

    /**
     * Navigation when the target page is the current page
     * When navigation is to the same page, simply update the input parameters. We don't
     * want to call Router.go so we can detect popstate event case in the navigated event
     * @param  {Page} page             the page object
     * @param  {Object} options        the navigateToPage action parameters
     * @param  {Object} oldInputParams an object representing the old input parameter values
     * @return {NavigateResult}        an object with the boolean property navigated
     */
    navigateToSamePage(page, options, oldInputParams) {
      const navigationPath = page.getNavPath();
      let navigated = Router.DID_NAVIGATE;
      let resultMsg = 'done';
      let reasonMsg = '';

      // Navigating to the same page without param is no-op, so restore the original
      // input param and bail out like nothing happen.
      if (!options.params) {
        this.history.setInputParameters(oldInputParams);
        resultMsg = 'cancelled';
        reasonMsg = 'because it is the current page and input parameters are undefined';
        navigated = Router.DID_NOT_NAVIGATE;
      } else {
        // Update the each flow/page in the current hierarchy with the new parameters
        page.refreshInputParameters();

        // There are 2 types of path for a page, one with the hidden shell page id (fullPath)
        // looking like shell/main/start and one without (getNavPath) looking like main/start.
        // Make sure to update the browser history with fullPath, not navigationPath. This is
        // because when the router handle the popState event in popStateEventListener, it
        // compares the fullPath of the current page, not the navigation path. Also note
        // that fullPath is also the value used when calling updateState in page.run()
        this.rootRouter.updateState(page.fullPath, false);

        this.application.previousPagePath = navigationPath;
        this.application.previousPageParams = Utils.cloneObject(this.history.getInputParameters());

        page.invokeAfterNavigateEvent({ previousPageParams: oldInputParams });

        Router.clearBusyState();
      }

      this.log.info('Navigation to', navigationPath, 'is', resultMsg, reasonMsg);
      return navigated;
    }

    /**
     * Navigation when the target page is different than the current page
     * @param  {Page} page             the page object
     * @param  {Object} options        the navigateToPage action parameters
     * @param  {Object} oldInputParams an object representing the old input parameter values
     * @return {NavigateResult}        an object with the boolean property navigated
     */
    navigateToOtherPage(page, options, oldInputParams) {
      const navigationPath = page.getNavPath();
      let goOptions;
      if (options.history === Constants.HistoryMode.REPLACE || options.history === Constants.HistoryMode.SKIP) {
        goOptions = { historyUpdate: options.history };
      }

      // Before initiating the router navigation, clear up the current search because of the
      // possible fromUrl input parameter. Make a copy of the search in case the navigation does
      // not succeed.
      const oldSearch = this.history.getSearch();
      this.history.resetUrlParameters();

      // full path to the destination page
      this.application.destinationPath = page.fullPath;

      // Because of default page defined using flow, the navigation path might be different
      // from original path so use the page fullPath.
      // Launch the navigation and return the outcome immediately without waiting for the
      // navigation to complete.
      this.rootRouter.go(navigationPath, goOptions)
        .then((result) => {
          if (result.hasChanged === false) {
            // If the navigated was vetoed by a beforeExit event, we need to clear the busy state
            Router.clearBusyState();
            // Restore search and input params if navigation was cancelled.
            this.history.setSearch(oldSearch);
            this.history.setInputParameters(oldInputParams);
            this.history.setSkipMode(false);
          } else {
            // If the window is currently scrolled, the browser behavior is to keep the scrolled
            // position. This is not desired when navigating, so scroll to the top.
            window.scroll(0, 0);
            // Remove existing hash from URL since JET router does not
            this.history.setHash();
            // Update the page parents with the new parameters. No need to do the page which
            // has been initialized with the correct input parameters.
            page.parent.refreshInputParameters(true);
          }
        })
        .catch((error) => {
          page.deletePageFromParent();
          // Restore search and input params if navigation failed.
          this.history.setSearch(oldSearch);
          this.history.setInputParameters(oldInputParams);
          this.history.setSkipMode(false);

          this.log.error(error);
        });

      return Router.DID_NAVIGATE;
    }

    /**
     * This function is called during the checkNavigable process to know if there is a need to check
     * that the target page is "navigable".
     * The default is false, packagePage and pageExtension have different implementation and require
     * the navigability to be check sometime.
     *
     * @return {boolean}  true when it should check the page is navigable
     */
    // eslint-disable-next-line class-methods-use-this
    isNavigationRestrictedToPublicPage() {
      return false;
    }

    /**
     * Determines whether this container is the default child of its parent.
     * Containers without default-route semantics are not default by default.
     * @return {boolean}
     * @protected
     */
    // eslint-disable-next-line class-methods-use-this
    isDefault() {
      return false;
    }

    /**
     * Returns true if the id is the default flow id for this container.
     * Containers without default-flow semantics are not default by default.
     * @param {string} id
     * @return {boolean}
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    isDefaultFlowId(id) {
      return false;
    }

    /**
     * Returns true when parent traversal should stop at this container.
     * @return {boolean}
     */
    // eslint-disable-next-line class-methods-use-this
    isNavigationBoundary() {
      return false;
    }

    /**
     * Return true if this container can be navigated to from the outside
     * @return {Boolean}
     * @protected
     */
    isNavigable() {
      const { navigation } = this.definition;
      if (navigation && navigation.fromExternal === Constants.FROM_EXTERNAL_NAVIGATION_ENABLED) {
        if (!this.parent.isNavigable()) {
          this.log.error('Navigation to parent', this.parent.getNavPath(), 'is not enabled.');
        }
        return true;
      }
      if (this.isDefault() && this.parent.isNavigable()) {
        return true;
      }

      return false;
    }

    /**
     * Returns an Array of objects that contain a (curried) this.callActionChain function.
     *
     * @param {Object} eventListenerDef either (new) contains an 'actions' array,
     * or (old) is an array, of objects with 'chainId'
     * @return {Array<{chainId: string, fnc: function}>}
     *
     */
    createListenerChainFunctions(eventListenerDef) {
      const functionWrappers = [];

      const listenerChains = eventListenerDef.chains || [];

      listenerChains.forEach((listenerAction) => {
        // setup the action chain and execute
        const { chainId, chain, parameters } = listenerAction;

        // curry a function that will invoke the action chain with the parameters
        // the function still requires the 'expressionContexts' argument
        const fnc = (expressionContexts) => this.callActionChain({ chain, chainId },
          parameters || {}, expressionContexts);

        functionWrappers.push({ chainId: chainId || chain, fnc });
      });
      return functionWrappers;
    }

    /**
     * call the functions returned from createListenerChainFunctions in parallel
     * this is the original behavior, for older non-declared events, and VB events
     *
     * @param {Array<{chainId: string, fnc: function}>} chainFunctionWrappers
     * @param {Object} expressionContexts
     * @return {Promise}
     */
    callListenerChainFunctionsInParallel(chainFunctionWrappers, expressionContexts) {
      // swallow the error here and defer to the action chains to report errors
      // todo: exception was being swallowed with no reporting;
      // reason for this change was user error, example used $page.functions in a
      // module calling callModuleFunctionAction ($page not defined).
      const promises = chainFunctionWrappers.map((wrapper) => wrapper.fnc(expressionContexts));
      return Promise.all(promises).catch((err) => {
        this.log.error(err);

        // alwaysRethrowException is set by functional test to allow checking for failure cases
        if ((/** @type {ApplicationWithRethrowFlag} */ (this.application)).alwaysRethrowException) {
          throw err;
        }

        return Promise.resolve();
      });
    }

    /**
     * Call the onVariableChange event listeners.
     *
     * NOTE: this method matches the functionality of the original 'callEventListeners', which was removed,
     * and split into createListenerChainFunctions/callListenerChainFunctionsInParallel.
     *
     * The name is changed because it is now called specifically for variables, and should not be used for
     * general event processing.
     * Events can now have 'behavior', which may determine how the chains should be called (serially, parallel, etc).
     *
     * @param {Object} eventListenerDef
     * @param {Object} expressionContexts
     * @returns {Promise}
     */
    callVariableEventListeners(eventListenerDef, expressionContexts) {
      const chainFunctionWrappers = this.createListenerChainFunctions(eventListenerDef);
      return this.callListenerChainFunctionsInParallel(chainFunctionWrappers, expressionContexts);
    }

    /**
     * Tracks an action-chain promise owned by this container so generated-id fragment cleanup can avoid disposing
     * container state while close/save/event chains are still unwinding.
     * @param {Promise} promise action-chain execution promise
     * @returns {Promise} the original promise
     */
    trackActionChainPromise(promise) {
      this.pendingActionChainPromises.add(promise);
      const removePromise = () => {
        this.pendingActionChainPromises.delete(promise);
      };
      promise.then(removePromise, removePromise);
      return promise;
    }

    /**
     * Waits for newly discovered pending action-chain promises, including chains spawned while waiting.
     * @param {() => Promise[]} getPromises returns the current pending action-chain promise snapshot
     * @param {Set<Promise>} [seen] promises already awaited by this drain
     * @returns {Promise<void>}
     */
    waitForPendingActionChains(getPromises, seen = new Set()) {
      const promises = getPromises().filter((promise) => !seen.has(promise));
      promises.forEach((promise) => seen.add(promise));
      return promises.length
        ? Promise.allSettled(promises).then(() => this.waitForPendingActionChains(getPromises, seen))
        : Promise.resolve();
    }

    /**
     * Returns action-chain promises owned directly by this container.
     * @returns {Promise[]}
     */
    getOwnPendingActionChainPromises() {
      return Array.from(this.pendingActionChainPromises);
    }

    /**
     * Returns the fragment bridge for containers that support fragment components.
     * @returns {FragmentBridge}
     */
    // eslint-disable-next-line class-methods-use-this
    getFragmentBridge() {
      throw undefinedMethodError('getFragmentBridge');
    }

    /**
     * Returns a named runtime capability when supported by the container.
     * @param {string} option
     * @returns {boolean|undefined}
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    getCapability(option) {
      return undefined;
    }

    /**
     * Loads this container's template when supported.
     * @returns {Promise}
     */
    // eslint-disable-next-line class-methods-use-this
    loadTemplate() {
      throw undefinedMethodError('loadTemplate');
    }

    /**
     * Returns child fragments for containers that can own fragments.
     * @returns {Array}
     */
    // eslint-disable-next-line class-methods-use-this
    getChildFragments() {
      return [];
    }

    /**
     * Deletes a child fragment from this container when supported by the container.
     * @param {string} id
     * @param {Fragment} [fragment] fragment instance expected at the id
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    deleteFragment(id, fragment) {
    }

    /**
     * Returns a snapshot of action-chain promises owned by this container and its child layouts/fragments.
     * @returns {Promise[]}
     */
    getPendingActionChainPromisesInSubtree() {
      const promises = Array.from(this.pendingActionChainPromises);

      (this.layouts || []).forEach((layout) => {
        if (layout && typeof layout.getPendingActionChainPromisesInSubtree === 'function') {
          promises.push(...layout.getPendingActionChainPromisesInSubtree());
        }
      });

      this.getChildFragments().forEach((fragment) => {
        if (fragment && typeof fragment.getPendingActionChainPromisesInSubtree === 'function') {
          promises.push(...fragment.getPendingActionChainPromisesInSubtree());
        }
      });

      return promises;
    }

    /**
     * Invokes an action chain with the given ID. The ID may include qualifiers to invoke
     * an action chain in different contexts.
     *
     * The parameters will be passed directly into the chain and may be expressions.
     *
     * A promise will be returned containing the name of the outcome and result from calling
     * that chain. Errors occurring during the chain will result in this promise being rejected.
     *
     * @final
     * @param chainLocator object containing chainId (JSON chain) or chain (JS chain)
     * @param params  The parameters to the chain
     * @param expressionContexts
     * @returns {Promise.<*>} The result of calling the action chain
     */
    callActionChain(chainLocator, params = {}, expressionContexts = this.getAvailableContexts()) {
      const context = {
        application: this.application,
        parent: this.parent,
        container: this,
        chains: this.chains,
      };

      const runner = ActionChainRunner.create(chainLocator);

      if (logger.isFiner) {
        logger.finer('action chain', runner.chainId, 'called with params', params, 'for event',
          (expressionContexts && expressionContexts[Constants.ContextName.EVENT]) || {});
      }

      // All callers of this method expect the result of an action chain to be an outcome.
      // However. that is not the case for a JS chain, so we pass in true for coerceResultToOutcome
      // to force the result to be an outcome.
      // Track the returned chain promise so generated-id fragment cleanup can wait for in-flight work before disposal.
      return this.trackActionChainPromise(runner.run(params, context, expressionContexts,
        { coerceResultToOutcome: true }));
    }

    /**
     * The complete set of variables {Context} for expression evaluation of variable default values during
     * variable initialization.
     *
     * This method is used by createVariable and invokeEvent in container.js.
     *
     * The container subclass should create the availableContexts object
     *
     * @return {Object} a map of expression contexts, mapped by $<page/flow/application/etc.>
     */
    getAvailableContexts() {
      if (!this.availableContexts) {
        // Call the static getAvailableContexts method on the context object (pageContext, flowContext, etc)
        // and returns the set of $xxxx variables available for binding and expressions
        this.availableContexts = Object.getPrototypeOf(this).constructor.ContextType.getAvailableContexts(this);
        // Propagate the getAvailableContexts call to all extensions
        this.traverseExtensions('getAvailableContexts');
      }
      return this.availableContexts;
    }

    /**
     * returns the ContextType constructor used to create the '$' expression context
     * @returns {ContainerContextType} constructor of type ContainerContext
     * @see loadMetadata
     */
    static get ContextType() {
      throw undefinedPropertyError('ContextType');
    }

    /**
     * the definition has been loaded, now create any event-related constructs
     */
    initializeEvents() {
      this.initializeEventDefinitions();
      this.initializeComponentListeners();

      // Propagate the initializeEvents call to all extensions
      this.traverseExtensions('initializeEvents');
    }

    /**
     * look at "events" and "componentEvents" and register the declarations
     */
    initializeEventDefinitions() {
      // process "events" and "componentEvents", at top level and in "interface"
      const interfaceEvents = this.definition.interface.events || {};
      Object.keys(interfaceEvents)
        .forEach((eventName) => {
          const def = interfaceEvents[eventName];
          // 'listenable' is the default for interface events
          if (!def.mode) {
            def.mode = Constants.EventMode.LISTENABLE;
          }
        });

      // Events defined in interface have precedence. There is an audit in DT to catch duplicates
      const allDefs = Object.assign({}, this.definition.events, interfaceEvents);

      Object.keys(allDefs)
        .forEach((eventName) => {
          const def = allDefs[eventName];
          const isInterface = !!interfaceEvents[eventName];
          const unregisterEventFct = EventRegistry.register(this, eventName, def, isInterface);
          if (unregisterEventFct) {
            this.unregisterEventArray.push(unregisterEventFct);
          }
        });
    }

    /**
     * creates a method for each listener in the $listeners function map.
     */
    initializeComponentListeners() {
      const { eventListeners } = this;
      Object.entries(this.definition.eventListeners).forEach(([eventListenerName, eventListenerDef]) => {
        /**
         * Invokes the action chain listener asynchronously, and evaluates some synchronous, optional, expression
         * properties (stopPropagation, preventDefault) to control DOM event behavior.
         * The synchronous properties do not affect calling the listener action chains, they only affect the behavior of
         * the DOM event.
         *
         * @param {Object} event
         * @param {Object} current
         * @param {Object} bindingContext
         */
        const executeEvent = (event, current, bindingContext) => {
          // Once exit/dispose has been called on a container (most likely a page since it's a component event)
          // then the container is unable to handle the event, so just ignore it.
          if (this.lifecycleState === Constants.ContainerState.EXITED
            || this.lifecycleState === Constants.ContainerState.DISPOSED) {
            settleIgnoredCallbackEvent(event);
            return;
          }

          // Normalize before cloning so cloneObject does not enumerate row-scoped runtime metadata while preserving
          // explicit access to it on the original event row.
          normalizeEventPayloadRowContextMetadata(event);

          // clone the event; methods won't work, but they don't work asynchronously, anyway.
          // this means 'currentTarget' will stick around
          // todo: revisit cloneObject(event)
          // when discussed, (cds) said just pass it as is, but with the previous 'ui' hack below, that seems
          // strange to modify the actual event.  also, I (mjb) would like to do a similar hack for the 'currentTarget',
          // which becomes null after the sync listener, but can be useful when sharing event listeners.
          const newEvent = Utils.cloneObject(event, {});
          preserveEventPayloadRowContextMetadata(event, newEvent);
          // Action chains evaluate listener parameters asynchronously. Snapshot DataTransfer from the original
          // event now, before the browser clears the protected drag data store at the end of the native drop event.
          if (event && event.dataTransfer) {
            newEvent.dataTransfer = snapshotDataTransfer(event.dataTransfer);
          }
          const asyncBehaviorFunction = (eventListenerDef && eventListenerDef.asyncBehaviorFunction) || 'accept';
          const asyncEvent = (event && event.detail && typeof event.detail[asyncBehaviorFunction] === 'function');
          // async event behavior has to be opted in by event listener - chain author, via this new property
          // 'asyncBehavior' on eventListeners. 'asyncBehaviorFunction' selects the event detail function that
          // receives the chain Promise and defaults to 'accept'.
          const asyncEventBehaviorEnabled = (asyncEvent
            && (eventListenerDef && eventListenerDef.asyncBehavior === 'enabled'));

          // make sure eventDispatcher is available via the context for dispatching CCA events:
          const eventDispatcher = Utils.getDispatcher(bindingContext);
          if (eventDispatcher && !this.availableContexts[Constants.ContextNameInternal.DISPATCH_EVENT]) {
            this.availableContexts[Constants.ContextNameInternal.DISPATCH_EVENT] = eventDispatcher;
          }

          const chainPromise = this.invokeComponentEvent(eventListenerName, eventListenerDef, newEvent,
            current, bindingContext, asyncEventBehaviorEnabled);

          // If the configured async behavior function is set, and event listener enabled the asyncBehavior, event is
          // async and the chain Promise is linked to the original event detail function. The default function is
          // 'accept' for backward compatibility. Example: oj-table onBeforeRowEdit event.
          // Otherwise it's ignored and event execution is assumed to be synchronous.
          if (asyncEventBehaviorEnabled) {
            // Note: some tests call the listener callback directly without providing an event. Guard against that.
            event.detail[asyncBehaviorFunction](chainPromise);
          }

          const expressionContexts = this.getAvailableContexts().clone();
          expressionContexts[Constants.ContextName.EVENT] = newEvent;

          // check if listener property 'preventDefault' expression is set.  If expression evaluates to true,
          // call event.preventDefault().
          // Note: VB has set a precedent with a synchronous event listener wherein, when preventDefault() is called (or
          // regardless of the value of 'preventDefault' property), the chain is always invoked. While this may seem
          // strange, it has led authors to start action chain with a condition, to abort the chain execution if
          // preventDefault outcome was true (duplicating the same conditional logic for 'preventDefault' prop at the
          // chains start). While is some cases it might be redundant in other cases it's quite possible that this
          // can't be avoided, because authors might still want to reset state / perform cleanup etc., in the event of
          // preventing default behavior.
          // The same behavior will be used for async events, with the recommendation that authors return a
          // failure outcome from the chain(s), post-preventDefault. This way the 'accept' Promise can be rejected.
          const preventDefault = evaluateSynchronousHandlerProperty(eventListenerDef.preventDefault,
            expressionContexts);

          if (preventDefault) {
            if (typeof event.preventDefault === 'function') {
              this.log.info('preventing default behavior for event in', eventListenerName, 'event listener');
              event.preventDefault();
            } else {
              this.log.warn(eventListenerName, 'event listener has a true \'preventDefault\' expression,',
                'but listener argument does not have \'preventDefault\' method');
            }
          }

          // next, check if listener property 'stopPropagation' expression.
          // NOTE: this expression cannot reference the current listeners result, since it is running asynchronously.
          // NOTE: the action chains have already been called at this point; these expressions do not prevent that
          const stopPropagation = evaluateSynchronousHandlerProperty(eventListenerDef.stopPropagation,
            expressionContexts);

          // if 'stopPropagation' expression is true, call event.stopPropagation
          if (stopPropagation) {
            if (typeof event.stopPropagation === 'function') {
              this.log.info('stopping event propagation in', eventListenerName, 'event listener');
              event.stopPropagation();
            } else {
              this.log.warn(eventListenerName,
                'event listener has a true \'stopPropagation\' expression,',
                ' but listener argument does not have \'stopPropagation\' method');
            }
          }
        };

        // Use a named arrow function (executeEvent) to make it easier to trace it when debugging or
        // looking for memory leaks
        eventListeners[eventListenerName] = executeEvent;
      });
    }

    /**
     * Invoke a page event listener using its name.
     * @param {string}    eventName the name of the event listener array
     * @param {*}         eventPayload the payload of the event
     * @param {*}         current the payload (if available) of the event (i.e. the iterable of a foreach)
     * @param {*}         bindingContext binding context of the event
     * @param {boolean}    enableAsyncBehavior if enabled, then link chain result to Promise
     * @return {Promise}  a promise that resolves when every listener in the array is done. Rejects when any
     * listener returned a failure outcome.
     */
    invokeComponentEvent(eventName, eventListenerDef, eventPayload, current, bindingContext,
      enableAsyncBehavior = false) {
      // Following code identify case where the event was dispatch from a anchor tag with a
      // href="#" and a on-click then set a flag on the VB history object so that the popstate
      // event caused by href="#" is ignored
      {
        // eventPayload is never null or undefined, see call in executeEvent.
        const { target } = eventPayload;
        if (target && target.tagName === 'A') {
          const href = target.getAttribute('href');
          // When preventDefault is set to true, there is no handling of the href, so no need to touch the history.
          if (href && eventListenerDef.preventDefault !== 'true' && href.startsWith('#')) {
            logger.error('Ignoring the href="#" in an anchor tag with on-click because it causes navigation issues.');
            this.history.foundHrefWithHash(true);
          }
        }
      }

      normalizeEventPayloadRowContextMetadata(eventPayload);

      // collect the variables
      const expressionContexts = this.getAvailableContexts().clone();

      // and add the $event, $current, and $eventContext properties
      expressionContexts[Constants.ContextName.EVENT] = eventPayload;
      expressionContexts[Constants.ContextName.CURRENT] = current;
      expressionContexts[Constants.ContextName.BINDING_CONTEXT] = bindingContext;

      logger.beforeHandleEvent(this.className, this.id,
        'handling component event', eventName, 'with payload:', eventPayload, 'and current:', current);

      const mo = new EventMonitorOptions(
        EventMonitorOptions.SPAN_NAMES.EVENT_COMPONENT, eventName, eventPayload, this,
      );

      return this.log.monitor(mo, (eventTime) => {
        // Set the busy context so the webdriver test know to wait on the event to be handled
        const busyContext = ojContext.getPageContext().getBusyContext();
        const busyStateResolver = busyContext.addBusyState({ description: `Event: ${eventName}` });
        const chainFunctionWrappers = this.createListenerChainFunctions(eventListenerDef);
        // callListenerChainFunctionsInParallel is the original behavior, that calls chains in parallel
        return this.callListenerChainFunctionsInParallel(chainFunctionWrappers, expressionContexts)
          .then((res) => {
            if (enableAsyncBehavior) {
              // when a listener returns a failure outcome the event accept promise is automatically rejected with the
              // failure message. This is new async event behavior!
              const failed = (res || []).find((o = {}) => o.name === Constants.ActionOutcomes.FAILURE);
              if (failed) {
                const msg = `chain execution for event ${eventName} failed with error: ${failed.result}`;
                throw (new Error(msg));
              }
            }
            logger.afterHandleEvent(this.className, this.id,
              'handled component event', eventName, 'successfully', eventTime());

            return res;
          })
          .catch((error) => {
            logger.afterHandleEvent('Failed to handle component event', eventName, eventTime(error));
            this.log.error(error);
          })
          .finally(() => {
            // The busy context might be already cleared from clearBusyContextBeforeDialog()
            // so drop the error.
            // eslint-disable-next-line no-empty, no-unused-vars
            try { busyStateResolver(); } catch (e) {}
            // For safety in case popState event was not called
            this.history.foundHrefWithHash(false);
          });
      });
    }

    /**
     * Called by the router to give the opportunity to the container to cancel navigation
     * on exit. The default implementation is to return true to accept the router transition.
     * @param {string} [origin] of the state change
     * @param {Object} [details] Additional info when origin is 'popState'
     * @return {Promise<boolean>} a promise that resolves to a boolean.
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    canExit(origin, details) {
      return Promise.resolve(true);
    }

    /**
     * Invoke an event using the current expression contexts
     *
     * @param {string} eventName the name of the event listener array
     * @param {*} [eventPayload] (optional) the payload of the event
     * @param {EventBehavior|*} [eventBehavior] (optional) only provided when the caller is EventBehavior
     * @param {*} [previousResult] (optional) only valid for events of type "transform"; the previous listener result
     * @return {Promise} a promise that resolves when every listener in the array is done
     */
    invokeEvent(eventName, eventPayload, eventBehavior, previousResult) {
      if (this.lifecycleState === Constants.ContainerState.DISPOSED
        || this.lifecycleState === Constants.ContainerState.EXITED) {
        return Promise.resolve();
      }

      if (eventName.startsWith(Constants.VB_EVENT_MARKER)) {
        Performance.timestamp(this.id, eventName);
      }
      const mo = new EventMonitorOptions(
        EventMonitorOptions.SPAN_NAMES.EVENT_RUNTIME, eventName, eventPayload, this,
      );
      return this.log.monitor(mo, (finish) => Promise.resolve()
        .then(() => {
          let returnValue;
          // Before vbEnter is dispatched, make sure any (possibly) needed bundles have been loaded
          if (eventName === Constants.ENTER_EVENT) {
            returnValue = BundleUtils.whenBundlesReady();
          }

          return returnValue;
        })
        .then(() => {
          const lifecycleEventPromises = [];

          const eventModel = eventBehavior && eventBehavior.eventModel;
          const eventListenerDef = this.findEventListener(eventName, eventModel);

          if (eventListenerDef) {
            normalizeEventPayloadRowContextMetadata(eventPayload);

            // get the expression context variables
            const expressionContexts = this.getAvailableContexts().clone();
            // and add the $event
            expressionContexts[Constants.ContextName.EVENT] = eventPayload;

            // $parameters only meaningful for vbBeforeEnter
            if (eventName === Constants.BEFORE_ENTER_EVENT) {
              const container = this.isExtension() && this.base ? this.base : this;
              Object.defineProperty(expressionContexts, Constants.ContextName.PARAMETERS, {
                get() {
                  // Use a lazy-getter pattern since $parameters is rarely used
                  delete this[Constants.ContextName.PARAMETERS];
                  Object.defineProperty(this, Constants.ContextName.PARAMETERS, {
                    value: container.buildAllParameters(),
                    enumerable: true,
                    configurable: true,
                  });

                  return this[Constants.ContextName.PARAMETERS];
                },
                enumerable: true,
                configurable: true,
              });
            }

            const chainFunctionWrappers = this.createListenerChainFunctions(eventListenerDef);
            let promise;

            if (eventBehavior) {
              promise = eventBehavior
                .callChainFunctions(this, chainFunctionWrappers, expressionContexts, previousResult);
              // because vbAfterNavigate is a bubbling event we also need to
              // traverse extensions so that their listeners are fired as well
              if (eventName !== Constants.AFTER_NAVIGATE_EVENT) {
                return promise;
              }
            } else {
              // this is used for internal ('vbXXX').
              promise = this.callListenerChainFunctionsInParallel(chainFunctionWrappers, expressionContexts);
            }

            lifecycleEventPromises.push(promise);
          }

          // VBS-1792 support vbEnter events for extensions
          // VBS-2088 support vbExit and vbAfterNavigate for extensions
          // VBS-6440 support vbBeforeEnter and vbBeforeExit for extensions
          if (eventName === Constants.ENTER_EVENT
            || eventName === Constants.EXIT_EVENT
            || eventName === Constants.AFTER_NAVIGATE_EVENT
            || eventName === Constants.BEFORE_ENTER_EVENT
            || eventName === Constants.BEFORE_EXIT_EVENT) {
            const promises = this
              .traverseExtensions('invokeEvent', eventName, eventPayload, eventBehavior, previousResult);
            lifecycleEventPromises.push(...promises);
          }

          // don't return array, keep pre VBS-1792 behavior, just in case
          return Promise.all(lifecycleEventPromises).then((results) => {
            const listenerResults = results.filter((r) => r !== Constants.NO_EVENT_LISTENER_RESPONSE);

            // if no listeners, return the marker; used when collecting (transform event) results.
            if (listenerResults.length === 0) {
              return Constants.NO_EVENT_LISTENER_RESPONSE;
            }

            if (eventName === Constants.BEFORE_ENTER_EVENT || eventName === Constants.BEFORE_EXIT_EVENT) {
              // in case of the vbBeforeEnter and vbBeforeExit events, all listener promises results
              // need to be combined so that we can check if any one of them was cancelled
              const combinedListenerResults = [];
              listenerResults.forEach((resultsArray) => {
                // In case of error during the execution of the event, the result can be undefined
                if (Array.isArray(resultsArray)) {
                  combinedListenerResults.push(...resultsArray);
                }
              });

              return combinedListenerResults;
            }

            return listenerResults[0];
          });
        })
        .then((outcome) => {
          if (eventName === Constants.ENTER_EVENT || eventName === Constants.EXIT_EVENT) {
            this.log.info(this.className, this.id, 'completed', eventName, finish());
          } else {
            finish();
          }
          return outcome;
        })
        .catch((err) => {
          if (eventName === Constants.ENTER_EVENT || eventName === Constants.EXIT_EVENT) {
            this.log.info(this.className, this.id, 'failed to complete', eventName, finish(err));
          } else {
            finish(err);
          }
          throw err;
        }));
    }

    /**
     * Finds an event listener for the given event name, if it exists.
     *
     * @param {String} eventName - The name of the event. This may or may not be qualified.
     * @param {EventModel} eventModel - The event definition object.
     * @returns {Object|null} The event listener definition if it exists, otherwise null.
     */
    findEventListener(eventName, eventModel) {
      return EventRegistry.findListener(this, eventName, eventModel);
    }

    /**
     * Return the definitions at the top level of the descriptor, as well as in the interface section
     * for artifact of type defName. Valid values are 'variables', 'constants', 'types'  and e'events'
     * @param {String} defName
     * @return {Object}
     */
    getAllDefinitionOf(defName) {
      // Artifacts defined in interface have precedence. There is an audit in DT to catch duplicates
      return Object.assign({}, this.definition[defName], this.definition.interface[defName]);
    }

    /**
     * Invoke a (page) event listener using its name.
     *
     * NOTE: this is currently implemented to allow either DECLARED or UNDECLARED events (the original events,
     * like lifecycle events, or custom and notification events).
     *
     * If the current page/flow does not have a listener, or the listener explicitly returns an outcome
     *  to indicate bubbling should continue (see 'shouldBubble'),
     * then, traverse up the containment, looking for additional listeners, as:
     *
     * a) this.parent (flows and pages can both have listeners).
     * b) continue to recurse to (a) where 'this' is the current flow 'this.parent' from (b)
     *
     * this method is called once; the recursion happens in EventBehavior.recurseContainmentAndCreateFunctions()
     *
     * @param {string} eventName
     * @param {*} eventPayload
     *
     * @returns {Promise} resolved value depends on the event behavior (if any). only used for 'transform' behavior
     */
    invokeEventWithBubbling(eventName, eventPayload) {
      if (this.lifecycleState === Constants.ContainerState.EXITED
        || this.lifecycleState === Constants.ContainerState.DISPOSED) {
        const detail = eventPayload && (eventPayload.detail || eventPayload);
        settleIgnoredCallbackEvent({
          type: eventName,
          detail,
        });
        return Promise.resolve();
      }

      return Promise.resolve()
        .then(() => {
          /**
           * start the actual recursion, looking for listeners, and optionally continuing up the containment
           */
          const eventModel = EventRegistry.get(this, this, eventName);

          // This is the container where we start the event propagation.
          // For declared events, we always start with the 'leaf' page in the containment chain.
          // For (legacy) undeclared custom events, we start with the container firing the event.
          // This difference is handled by the EventBehavior.

          if (!eventModel) {
            // this means the event was declared , but is not usable by us (declared in a child container).
            throw new Error(`Event registry returned null for event ${eventName}`);
          }

          // finding an eventModel without a behavior should not happen here, but check, just in case
          if (!eventModel.eventBehavior) {
            throw new Error(`No behavior found for event ${eventName}`);
          }

          if (!eventModel.eventBehavior.triggerableBy(this)) {
            throw new Error(`Event '${eventName}' is not triggerable from container '${this.id}'`);
          }

          // if the event is configured for auto-wiring, ensure the event container supports it
          if (eventModel.isAutoWireEnabled() && !eventModel.container.allowsEventAutoWiring(eventModel)) {
            this.log.warn(`Event '${eventName}' is configured for auto-wiring but container `
              + `'${eventModel.container.id}' doesn't support it.`);
          }

          return eventModel.eventBehavior.start(this, eventName, eventPayload);
        });
    }

    /**
     * Initializes action chains from metadata.
     *
     */
    initializeActionChains() {
      this.chains = this.definition.chains || {};
      // Propagate the initializeActionChains call to all extensions
      this.traverseExtensions('initializeActionChains');
    }

    /**
     * Initialize variables of a specific namespace
     * @param  {Object} variablesDef the definition of the variables loaded from the container json
     * @param  {String} namespace  the namespace, "variables", "metadata", See Constants.VariableNamespace
     * @param  {Object} [descriptor] an object with additional option to be applied to the variable
     * @return {Promise}           a promise that resolves when all variables are initialized
     */
    // eslint-disable-next-line default-param-last
    initializeVariableByNamespace(variablesDef = {}, namespace, descriptor) {
      const variableNames = Object.keys(variablesDef);
      if (variableNames.length === 0) {
        return Promise.resolve();
      }

      const promises = [];

      // build variable dependency graph
      // TODO: as a performance optimization this can be cached for the page so it's not recalculated every time the
      // page is visited
      const variablesDepsMap = StateUtils.buildVariablesDependenciesGraph(variablesDef, this.scopeResolver);

      // go through the variables and create each one.
      // this assumes the variable definition has been filtered by initDefault()
      variableNames.forEach((variableName) => {
        const variableDef = variablesDef[variableName];
        const d = descriptor || {};
        d.dependencies = variablesDepsMap && variablesDepsMap[variableName];

        // warning for metadata descriptors defined in variable namespace
        if (namespace === Constants.VariableNamespace.VARIABLES
            && INVALID_VARIABLE_METADATA_DESCRIPTOR_TYPES.includes(variableDef.type)) {
          this.log.error(`Cannot define a variable of type ${variableDef.type} for variable ${variableName}`);
        }

        // metadata variables are not persisted, and don't come from the URL
        if (namespace === Constants.VariableNamespace.METADATA) {
          delete variableDef.input;
          delete variableDef.persisted;
          delete variableDef.rateLimit;
        }

        promises.push(this.createVariable(variableName, variableDef, namespace, d));
      });

      // return when all the variables have been created
      return Promise.all(promises);
    }

    /**
     * Create a scope to store the variables of this container
     */
    initVariableScope() {
      // For page scopes, the name used is the name of the page, not 'page' since there is a
      // point during the transition between page where both page scope exist.
      this.scope = Scope.createScope(this.getNewScopeName(), this);

      // create the scope Context, which contains facades, if it hasn't been created yet
      this.getAvailableContexts();

      // Propagate the initVariableScope call to all extensions
      this.traverseExtensions('initVariableScope');
    }

    /**
     * Initialize variables in each namespace
     * @return {Promise} a promise that resolves when all variables are initialized.
     */
    async initAllVariableNamespace() {
      if (this.scope) {
        this.log.error('Scope already created');
        this.disposeScope();
      }
      this.initializeEnums();
      this.initVariableScope();

      // Order of initialization is:
      //   1) constants
      //   2) builtins
      //   3) variables
      //   4) metadata
      this.initializeConstants();
      await this.initializeBuiltins();

      // Return a promise so that the router will not enter the nested page until
      // the flow enter event is done executing. This is so the flow variables are
      // initialized with their final value before the page access them.
      await this.initializeVariables();

      await this.initializeMetadata();
      // Make sure translation bundles are ready
      await BundleUtils.whenBundlesReady();
      this.addScopeToStore();

      // need to add VCLs after store has been added, as it attempts to get variable (and its value?) from the scope.
      // Also setting up VCL if rules are present anywhere in the extension hierarchy
      if (this.supportsRules(true)) {
        this.getRulesEngine().addRulesValueChangeListeners(this.availableContexts);
      }

      this.scope.initializeVariablesComputed();

      await this.activateVariables();
      // activation of the dirty data tracking should only happen after
      // all of the variables have been activated to guarantee that variables
      // have been properly initialized
      this.activateDirtyDataTracking();

      this.initializeAiEventListeners();
      await this.initializeWebSockets();
    }

    /**
     * Registers AI response and request event listeners with the AI event bus.
     */
    initializeAiEventListeners() {
      const { aiEventBus } = this.application;
      const responseDefs = this.getAllDefinitionOf('aiResponseEventListeners');
      const requestDefs = this.getAllDefinitionOf('aiRequestEventListeners');

      aiEventBus.addAiResponseEventListeners(responseDefs, /** @type {*} */ (this));
      aiEventBus.addAiRequestEventListeners(requestDefs, /** @type {*} */ (this));
    }

    /**
     * Initializes all web sockets defined in the container's metadata.
     *
     * @async
     * @return {Promise} A promise that resolves when all web sockets have been initialized.
     */
    async initializeWebSockets() {
      const allDefs = this.getAllDefinitionOf('webSockets');
      const promises = [];

      Object.entries(allDefs).forEach(([name, def]) => {
        const ws = new VbWebSocket(name, def, this);
        this.webSockets[name] = ws;
        promises.push(ws.activate());
      });

      return Promise.all(promises);
    }

    /**
     * Activates the dirty data variable tracking for the given scope.
     */
    activateDirtyDataTracking() {
      this.scope.initVarsMarkedForDirtyDataTracking();
      this.traverseExtensions('activateDirtyDataTracking');
    }

    /**
     * Checks whether the current container has any dirty data variables.
     * It will check its current scope, then child containers and
     * any possible extensions.
     * @return {String} 'dirty' if there is at least one dirty data variable found;
     *                  'notDirty' otherwise
     */
    getDirtyDataStatus() {
      let isDirtyData = this.checkForDirtyData();

      if (isDirtyData) {
        // the current container has dirty data we are done
        return Constants.DirtyDataReturnStatus.DIRTY;
      }

      // traverse the hierarchy of child containers and
      // check if they have any dirty data
      let nextContainer = this.getLeafContainer();

      while (nextContainer && nextContainer !== this) {
        isDirtyData = nextContainer.checkForDirtyData();

        if (isDirtyData) {
          // found a container with dirty data so we are done
          return Constants.DirtyDataReturnStatus.DIRTY;
        }

        // no dirty data so far, get next container
        nextContainer = nextContainer.parent;
      }

      // didn't find any dirty data
      return Constants.DirtyDataReturnStatus.NOT_DIRTY;
    }

    /**
     * Checks whether the current container has any dirty data variables.
     * It will check its current scope, then any embedded layouts and
     * their extensions if present.
     * @return {boolean} true if there is at least one dirty data variable found;
     *                   false otherwise
     */
    checkForDirtyData() {
      // first check current container scope
      if (this.scope && this.scope.checkForDirtyData()) {
        return true;
      }

      // no dirty data so far, so now check if the current
      // container has any layouts and if they have any dirty data
      const hasDirtyData = this.layouts.find((layout) => layout.checkForDirtyData());
      if (hasDirtyData) {
        return true;
      }

      // still no dirty data so far, so now check if there are any
      // extensions of the current container and if they have any dirty data
      const dirtyDataInExtensions = this.traverseExtensions('checkForDirtyData');
      return dirtyDataInExtensions.includes(true);
    }

    /**
     * Re-sets the dirty data state of variables in the current container's scope
     * as well as in all contained containers including their extensions.
     */
    resetAllDirtyData() {
      this.resetDirtyData();

      // traverse the hierarchy of child containers and reset any dirty data
      let nextContainer = this.getLeafContainer();

      while (nextContainer && nextContainer !== this) {
        nextContainer.resetDirtyData();
        nextContainer = nextContainer.parent;
      }
    }

    /**
     * Re-sets the dirty data state of variables in the current container's scope
     * as well as layouts and their extensions.
     */
    resetDirtyData() {
      // first reset current container scope
      if (!this.scope) {
        this.log.error('Cannot reset dirty data state of variables in the', this.className, this.id,
          'container because scope is invalid.');
        return;
      }

      this.scope.resetDirtyData();

      // now reset all layout scopes
      this.layouts.forEach((layout) => {
        layout.resetDirtyData();
      });

      // finally go through all current extensions
      this.traverseExtensions('resetDirtyData');
    }

    /**
     * Enums are considered types and defined as such, so go through all of the defined types, find
     * enums and initialize them.
     */
    initializeEnums() {
      // types defined in interface have precedence, there is an audit in DT to catch duplicates
      const allDefs = this.getAllDefinitionOf('types');

      // go through the types and look for enums
      const entries = Object.entries(allDefs);
      entries.forEach((entry) => {
        const type = entry[0];
        const typeDef = entry[1];
        if (typeDef && typeDef.enumType) {
          // found an enum, need to ensure it's of supported type
          // currently only primitive types are supported, i.e., string, boolean, number
          if (Utils.isPrimitiveType(typeDef.enumType)) {
            // check whether the enum initializers are entirely left off, i.e.:
            // values: [Up, Down, Left, Right]
            // if this is the case, we need to auto-initialize them
            if (Array.isArray(typeDef.values)) {
              // if values are specified via an array, it's using a shortcut
              if (typeDef.enumType === 'number') {
                // enum is of type number, auto-increment starting with 0 index
                let index = 0;
                this.enums[type] = Object.assign({}, ...typeDef.values.map((key) => {
                  const obj = { [key]: index };
                  index += 1;
                  return obj;
                }));
              } else if (typeDef.enumType === 'string') {
                // enum is of type string, the values will be equivalent to its key
                this.enums[type] = Object.assign({}, ...typeDef.values.map((key) => ({ [key]: key })));
              } else {
                // otherwise log an error
                this.log.error(`Enum ${type} is missing initializers.`,
                  'Only enumerations of type number or string can be defined with no initializers.');
              }
            } else {
              this.enums[type] = typeDef.values;
            }
          } else {
            this.log.error(`Enum ${type} is of unsupported enumeration type ${typeDef.enumType}.`,
              'Only primitive types are supported.');
          }
        }
      });

      // Propagate the initializeEnums call to all extensions
      this.traverseExtensions('initializeEnums');
    }

    addScopeToStore() {
      // Create a local store
      StoreManager.addScopeToStore(this.scope);
      // Extension store need to be created before subscribing variables
      this.traverseExtensions('addScopeToStore');
    }

    /**
     * Activate all variables for the current container and its extension(s)
     * @return {Promise} that resolves when all variables are active
     * @private
     */
    async activateVariables() {
      const availableContexts = this.getAvailableContexts();

      await this.scope.activateVariables(availableContexts);
      return Promise.all(this.traverseExtensions('activateVariables'));
    }

    /**
     * Create a constant for the "info" builtins. For flows, the info object has 2 properties,
     * id and description
     * @param {Object} varDef
     */
    createInfoBuiltinVariable(varDef) {
      if (varDef) {
        this.createConstant(Constants.INFO_CONTEXT, {
          type: 'object',
          defaultValue: varDef,
        },
        Constants.VariableNamespace.BUILTIN);
      }
    }

    /**
     * Initialize built-in variables.
     */
    async initializeBuiltins() {
      const varDef = await this.defineInfoBuiltinVariable();
      this.createInfoBuiltinVariable(varDef);

      const currentPageDef = this.defineCurrentPageBuiltinVariable();
      // Only applicationClass and flow have the currentPage builtin variable
      if (currentPageDef) {
        this.scope.createVariable(Constants.CURRENT_PAGE_VARIABLE, Constants.VariableNamespace.BUILTIN,
          currentPageDef.type, currentPageDef.defaultValue, undefined, { writable: false });
      }
    }

    /**
     * Define the info builtin variable object
     * This is used by subclass to add properties to the info builtin object
     * @return Promise<Object> a promise that resolve with the builtin info variable value
     */
    // eslint-disable-next-line class-methods-use-this
    async defineInfoBuiltinVariable() {
      return null;
    }

    /**
     * By default currentPage is not defined. Only flow and application have an implementation.
     * App UI don't need the builtin variable either since it's defined as an alias of application
     * in packageContext
     * @returns {{type: string, defaultValue: *}|null}
     */
    // eslint-disable-next-line class-methods-use-this
    defineCurrentPageBuiltinVariable() {
      return null;
    }

    /**
     * Initialize constants from declaration.
     */
    initializeConstants() {
      // Constants defined in interface have precedence. There is an audit in DT to catch duplicates
      const allDefs = this.getAllDefinitionOf('constants');

      // go through the constants and create each one.
      // this assumes the constant definition has been filtered by initDefault()
      Object.keys(allDefs).forEach((constantName) => {
        this.createConstant(constantName, allDefs[constantName]);
      });

      // setup rulesSets for interface constants
      const { constants } = this.definition.interface;
      if (constants && Object.keys(constants).length && this.supportsRules(true /* anyLevel */)) {
        this.getRulesEngine().setupRuleSets(this, constants);
      }

      // Propagate the initializeConstants call to all extensions
      this.traverseExtensions('initializeConstants');
    }

    /**
     * Create a constant and store it in the scope
     * @param  {String}    constantName    the name of the constant
     * @param  {Object}    constantDef     the definition of the constant
     * @param  {String}    namespace       the variable namespace, either "constants" or "builtin"
     */
    createConstant(constantName, constantDef, namespace = Constants.VariableNamespace.CONSTANTS) {
      if (!this.checkVariableOrConstantNameIsValid(constantName)) {
        return;
      }

      // Constants cannot be of a VB type
      if (Utils.isInstanceType(constantDef.type)) {
        throw new Error(`Type cannot be a built-in type for constant ${constantName} in`
          + `${this.className} ${this.fullPath || this.definition.id}.`);
      }

      // If the constant has been extended, the availableContexts is coming from the extension
      // so that the potential expression in defaultValue is evaluated in the context of the extension.
      const contextSource = constantDef.extension || this;

      const defaultValue = StateUtils.createNonInstanceTypeDefaultValue(constantName, constantDef,
        this.scopeResolver, contextSource.getAvailableContexts());
      let finalDefaultValue = defaultValue;

      // an interface constant sets up a generic rules expression that will get resolved later in the
      // lifecycle. The above defaultValue expression is still built, and used as needed.
      const { rulesScopes } = constantDef;
      let useRules = false;
      if (rulesScopes) {
        const topRuleScope = rulesScopes && rulesScopes[0];
        // if the leaf-most extension sets a defaultValue it will always be honored over any rules that may be
        // defined in a lower order extension for backwards compatibility. Never would the rules for this constant run.
        if (isValueFromInline(topRuleScope)) {
          finalDefaultValue = defaultValue;
        } else {
          useRules = true;
          // set up an internal expression - e.g., $base.getVariable('foo', 'constants').rulesValue. This evaluates
          // to undefined until a value is provided by the rules engine
          // This expression is needed to allow live updates to dependents. Also without an expression it is never
          // part of the variablesDef. See scope.createConstant
          const rulesContextSource = topRuleScope.extension.getAvailableContexts();
          const rulesExpression = `{{ $base.getVariable('${constantName}', 'constants').rulesValue }}`;
          finalDefaultValue = StateUtils.buildVariableDefaults(constantName, rulesContextSource,
            constantDef.type, rulesExpression);

          // when a lower order extension has defaultValue, it may need to be evaluated by rules. So store it.
          const defaultValueRuleScope = rulesScopes.find((rs) => isValueFromInline(rs));
          if (defaultValueRuleScope) {
            defaultValueRuleScope.defaultValue = defaultValue;
            defaultValueRuleScope.extension = defaultValueRuleScope.extension || constantDef.extension;
          }
        }
      }

      // Input Value management: "fromCaller" or "fromUrl"
      const inputParameterValue = this.manageInputParameter(constantName, constantDef, finalDefaultValue);
      if (this.log.isInfo && inputParameterValue !== undefined) {
        this.log.info('Loaded', constantDef.input, 'constant', Utils.getWebStorageItemName(this.application.id,
          this.scope.name, Constants.VariableNamespace.CONSTANTS, constantName), 'value:', inputParameterValue);
      }

      const newConstant = this.scope.createConstant(constantName, namespace,
        constantDef.type, finalDefaultValue, inputParameterValue, constantDef.persisted, undefined, { useRules });

      // handle constant eventing
      // NOTE here the current container's available context is passed so that
      // the onValueChanged listener is evaluated in the proper context
      // the proper context will be automatically retrieved for any possible
      // onValueChanged listeners in the extensions
      this.addValueChangedListeners(newConstant, constantDef, this.getAvailableContexts());
    }

    /**
     * Initializes variables from declarations.
     *
     * @returns {Promise} A promise that resolves when all variables are initialized
     */
    initializeVariables() {
      const allDefs = this.getAllDefinitionOf('variables');
      return Promise.all([
        this.initializeVariableByNamespace(allDefs, Constants.VariableNamespace.VARIABLES),
        ...this.traverseExtensions('initializeVariables'),
      ]);
    }

    /**
     * Initializes metadata declarations.
     *
     * @returns {Promise} A promise that resolves when complete
     */
    initializeMetadata() {
      const promises = [];
      promises.push(this.initializeVariableByNamespace(this.definition.metadata,
        Constants.VariableNamespace.METADATA,
        { writable: false }));
      promises.push(...this.traverseExtensions('initializeMetadata'));

      return Promise.all(promises);
    }

    /**
     * Do not allow variable or constant to be the name of any of the
     * reserved URL parameters used by DT.
     *
     * @param  {String}   name    the variable name
     * @return {boolean}  true if the name is valid
     */
    checkVariableOrConstantNameIsValid(name) {
      if (Constants.ReservedVariableNames.includes(name)) {
        this.log.error('Variable or constant', name, 'is not valid because it is using a reserved name.');
        return false;
      }

      return true;
    }

    /**
     * Creates a new variable with the proper default value, input handling, and persistence handling.
     *
     * @private
     * @param {String} variableName The name of the variable
     * @param {Object} variableDef The structure of the variable definition in the page model
     * @param {String} [namespace=Constants.VariableNamespace.VARIABLES]
     * @param {Object} [descriptor] example: {writable: false}. overrides other values.
     *   - dependencies, a Map of dependencies (on other variables) for the current variable.
     *     See StateUtils.buildVariablesDependenciesGraph
     * @returns {Promise} A promise that resolves to a new variable
     */
    // eslint-disable-next-line default-param-last
    createVariable(variableName, variableDef, namespace = Constants.VariableNamespace.VARIABLES, descriptor) {
      if (!this.checkVariableOrConstantNameIsValid(variableName)) {
        return Promise.resolve();
      }

      const availableContexts = this.getAvailableContexts();
      const inputParamValue = this.getInputParameterFromCallerValue(variableName, variableDef);

      // determine the default value for this variable then create the variable. Also provide any input param that
      // was passed in that would be meaningful when creating the defaultValue.
      return StateUtils.createVariableDefaultValue(variableName, variableDef, this.scopeResolver,
        this.scope, availableContexts, namespace, inputParamValue)
        .then((defaultValue) => {
          // Input Value management: "fromCaller" or "fromUrl"
          // TODO: pavi: input parameter reconciliation does not work for defaultValue that is an instance -
          //  extendedType, factoryType, or 'any' random type today.
          //  - For instance factory type the caller should be able to pass in value for 'constructorParams'
          //  - For extended type the caller should be able to pass in value for 'defaultValue'
          //  - random type instances use default constructor, so no input parameter can be passed in!
          const inputParameterValue = this.manageInputParameter(variableName, variableDef, defaultValue);
          if (this.log.isInfo && inputParameterValue !== undefined) {
            this.log.info('Loaded', variableDef.input, 'variable',
              Utils.getWebStorageItemName(this.application.id, this.scope.name, namespace, variableName),
              'value:', inputParameterValue);
          }

          // create a reference variable if needed, at the moment this is only utilized
          // by fragments when extended types, i.e. SDPs, are passed in as parameters
          if (this.createReferenceVariable(variableName, namespace, inputParameterValue)) {
            return undefined;
          }

          // get the type
          const type = StateUtils.getType(variableName, variableDef, this.scopeResolver);

          // use 'descriptor', if provided. values in 'descriptor' override others
          const varDescriptor = this.getVariableDescriptor(variableDef, descriptor);

          // create the variable
          const newVariable = this.scope.createVariable(variableName, namespace,
            type, defaultValue, inputParameterValue, varDescriptor, variableDef);

          // handle variable eventing
          this.addValueChangedListeners(newVariable, variableDef, availableContexts);

          return newVariable;
        });
    }

    /**
     * Create and add a variable that references another variable.
     *
     * @param {string} variableName - The name of the reference variable.
     * @param {string} namespace - The namespace for the reference variable.
     * @param {*} initialValue - The initial value of the variable being referenced.
     * @returns {boolean} True if a reference variable was added, false otherwise.
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    createReferenceVariable(variableName, namespace, initialValue) {
      return false;
    }

    // eslint-disable-next-line class-methods-use-this
    getVariableDescriptor(variableDef, descriptor) {
      return Object.assign({}, {
        persisted: variableDef.persisted,
        input: variableDef.input,
        rateLimit: variableDef.rateLimit,
        dirtyDataBehavior: variableDef.dirtyDataBehavior,
      }, descriptor);
    }

    /**
     * Add valueChanged listeners for the variable or constant if explicitly configured.
     * It will also add listeners defined in extensions.
     * Note: This is also called for internal variables setup for builtin types.
     * @see StateUtils.instantiateType
     * @param newVariable (can be either a constant or variable)
     * @param variableDef
     * @param availableContexts
     */
    addValueChangedListeners(newVariable, variableDef, availableContexts) {
      const onValueChangedDef = variableDef.onValueChanged;
      if (onValueChangedDef) {
        this.handleValueChangedListener(newVariable, onValueChangedDef, availableContexts);
      }

      // even if there is no onValueChanged listener in the base, if the given variable/constant is defined in
      // the interface, we have to go through all extensions and look for the onValueChanged listeners there
      const { name } = newVariable;
      // Note DT doesn't allow the same name for both variable and constant, so only one can exist
      const isExtendable = !!(this.definition.interface.variables[name] || this.definition.interface.constants[name]);

      if (isExtendable) {
        // since this variable/constant is defined in the interface, we now need to check
        // whether it has been extended by any extensions
        this.traverseExtensions('addExtensionValueChangedListeners', newVariable);
      }
    }

    /**
     * Called to determine if an event behavior is supported by container. Most are except for dynamicComponent
     * behavior that is only supported on select container. See Layout and Fragment.
     * @param eventBehavior
     * @return {boolean}
     */
    // eslint-disable-next-line class-methods-use-this
    allowsDynamicComponentEventBehavior(eventBehavior) {
      return eventBehavior !== Constants.EventBehaviors.DYNAMIC_COMP;
    }

    /**
     * Whether container allows the event. undeclared events have been historically allowed for most containers
     * @param eventModel
     * @return {boolean}
     */
    // eslint-disable-next-line class-methods-use-this,no-unused-vars
    canFireEvent(eventModel) {
      return true;
    }

    /**
     * Whether the propagation behavior is supported by current container. Currently this property is only supported
     * on fragment and layout. Called from fireCustomEventAction.
     * @param type of propagation behavior being checked for. See Constants.EventPropagationBehaviors enum.
     * @param eventDef event definition
     * @return {boolean} true if behavior matches requested type
     */
    // eslint-disable-next-line class-methods-use-this,no-unused-vars
    allowsEventPropagation(type, eventDef) {
      // for now this is only supported on specific containers like fragment and layout
      return false;
    }

    /**
     * Called to determine if the event auto-wiring is supported by the current container.
     * Currently the auto-wiring of events is only supported by fragments.
     * @param eventDef event definition
     * @returns {boolean} true if the event auto-wiring is allowed; false otherwise
     */
    // eslint-disable-next-line class-methods-use-this,no-unused-vars
    allowsEventAutoWiring(eventDef) {
      return false;
    }

    /**
     * Adds the specified onValueChanged listener to the given variable or constant.
     * @param newVariable variable object representing a newly created variable
     * @param onValueChangedDef onValueChanged listener defined in the variable definition of the page model
     * @param availableContexts map of expression contexts
     */
    handleValueChangedListener(newVariable, onValueChangedDef, availableContexts) {
      if (!newVariable.onValueChanged) {
        this.log.warn(newVariable.name, 'does not have a live expression so its onValueChanged listener is ignored');
      } else {
        const varChangeTracker = {
          eventSource: newVariable.onValueChanged,
          eventListener: (e) => {
            const context = availableContexts.clone();
            context[Constants.ContextName.EVENT] = e; // capture event payload on the scope
            const eventName = e.type; // vb event type and name is the same
            logger.beforeHandleEvent(this.className, this.id,
              'handling variable event', eventName, 'with payload:', e);
            const mo = new EventMonitorOptions(EventMonitorOptions.SPAN_NAMES.EVENT_VARIABLE, eventName, e, this);
            return this.log.monitor(mo, (eventTime) => {
              this.callVariableEventListeners(onValueChangedDef, context).then(() => {
                logger.afterHandleEvent(this.className, this.id,
                  'handled variable event', eventName, 'successfully', eventTime());
              }).catch((error) => {
                logger.afterHandleEvent('Failed to handle variable event', eventName, eventTime(error));
                this.log.error(error);
              });
            });
          },
        };
        newVariable.addValueChangedListener(varChangeTracker.eventListener, this);
        this.variablesListeners.push(varChangeTracker);
      }
    }

    /**
     * Add the onValueChanged event listeners for the variable/constant in extensions.
     * @param newVariable variable for which to add the onValueChanged event listener
     */
    addExtensionValueChangedListeners(newVariable) {
      const { name } = newVariable;

      // determine whether the extensions variables/constants are defined
      const variables = this.definition.extensions.variables || {};
      const constants = this.definition.extensions.constants || {};
      if (variables || constants) {
        // now determine whether the given variable/constant has been extended by this extension container
        // and has the onValueChanged listener defined
        const extVar = variables[name] || constants[name];
        if (extVar && extVar.onValueChanged) {
          // the extended variable/constant has the onValueChanged listener defined in this extension container
          this.handleValueChangedListener(newVariable, extVar.onValueChanged, this.getAvailableContexts());
        }
      }
    }

    /**
     * if there is an imports declaration, load the (requireJS) modules.
     * This is how JET components, jetmodules and css modules are imported declaratively:
     *
     *  "imports": {
     *    "components": {
     *      "oj-button": {
     *          "path": "ojs/ojbutton"
     *      }
     *    },
     *     "modules" : {
     *       "converterutilsI18n": {
     *         "path": "ojs/ojconverterutils-i18n"
     *       }
     *     },
     *     "css" : [
     *        "/pages/resources/css/shell.css",
     *        "./resources/css/shell2.css",
     *        "{cdn}/fnd/gallery/2110.0.0/images/iconfont/ojuxIconFont.min.css"
     *    ]
     *  }
     * @returns {Promise}
     */
    loadImports() {
      return Promise.resolve().then(() => {
        const { imports } = this.definition;
        if (imports) {
          const promises = [];

          // initialize any components (JET or CCAs)
          if (imports.components) {
            const promise = this.importModules(imports.components);

            if (promise) {
              promises.push(promise);
            }
          }

          // load any other modules
          if (imports.modules) {
            const promise = this.importModules(imports.modules, { type: 'module', storeImport: true });

            if (promise) {
              promises.push(promise);
            }
          }

          // load any css
          if (imports.css) {
            promises.push(this.importCss(imports.css));
          }
          return Promise.all(promises);
        }

        return undefined;
      });
    }

    /**
     * Load any translation bundles.
     * Subclasses override to add specific options.
     * @param {Object} [options] Special directives for the bundle definitions
     * @param {boolean} [options.preferV1] If V1 translations config is present, ignore V2 translations config.
     * @param {boolean} [options.proxyBundles] Make proxies for bundles and don't wait for the bundles to be loaded
     * @param {Object} [options.replacementValues] (V1) map of key/value pairs for expressions in path
     * @param {boolean} [options.allowSelfRelative] (V1) let the container JSON use "./" to mean 'relative to the
     * same folder'
     * @param {boolean} [options.isUnrestrictedRelative] (V1) you can reach outside of your current folder
     * (only app-flow)
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     *
     * @returns {Promise}
     */
    loadTranslationBundles(options) {
      if (!this.loadBundlesPromise) {
        const defaultOptions = { initParams: this.application.initParams };
        const bundlesOptions = options ? Object.assign(defaultOptions, options) : defaultOptions;

        // todo : define system vars, for substitutions in path
        this.bundles = BundlesModel.createBundlesModel(this.application.runtimeEnvironment,
          this.definition,
          this.getResourceFolder(),
          bundlesOptions,
          this.extension);

        // Initiate the load of the translation bundles.
        // Caller may or may not wait, this.bundles has been assigned.
        this.loadBundlesPromise = this.bundles.load();
      }
      return this.loadBundlesPromise;
    }

    /**
     * Returns the caller provided value for the input params.
     * @param {String} variableName
     * @param {Object} variableDef
     * @return {*}
     */
    // eslint-disable-next-line no-unused-vars
    getInputParameterFromCallerValue(variableName, variableDef) {
      // first load the value from runtimeEnvironment which takes precedence over everything else
      // NOTE: This is used by the DT to pass parameters between pages shown in different iframes
      const inputParams = this.application.runtimeEnvironment.loadInputParameters();
      return inputParams && inputParams[variableName];
    }

    getInputParameterValue(variableName, variableDef) {
      let paramValue;
      let inputParams;
      let fromCallerValue = this.getInputParameterFromCallerValue(variableName, variableDef);

      if (fromCallerValue === undefined) {
        inputParams = this.history.getInputParameters();
        fromCallerValue = inputParams && inputParams[variableName];
      }

      // If the page variable is 'fromCaller' return the input param
      if (variableDef.input === Constants.VariablePropertyInput.FROM_CALLER) {
        paramValue = fromCallerValue;
        // If the page variable is 'fromUrl' and a URL parameter with this name exist,
        // replace the variable default value with input parameter value.
      } else if (variableDef.input === Constants.VariablePropertyInput.FROM_URL) {
        // 'fromCaller' has precedence over 'fromUrl'
        // Also if inputParams is an empty object versus null or undefined, it means each of
        // the input param should be set to undefined so that the potential
        // default value is restored.
        if (fromCallerValue !== undefined
          || (Utils.isObject(inputParams) && Object.keys(inputParams).length === 0)) {
          paramValue = fromCallerValue;
        } else {
          const fromUrlValue = this.history.getUrlParameter(variableName);
          if (fromUrlValue !== undefined) {
            paramValue = fromUrlValue;
          }
        }
      }

      return paramValue;
    }

    /**
     * Retrieve the input parameter value for a variable of type 'fromCaller' or 'fromUrl'.
     * This method does two things:
     *   1) return the variable value if one can be found using input parameter or URL.
     *   2) for variable of input type fromUrl, save the possible value on the URL.
     * For a variable with input fromUrl, the fromCaller value has precedence.
     *
     * @param  {string}    variableName name of the variable
     * @param  {object}    variableDef  definition of the variable from descriptor
     * @param  {object|function} defaultValue the default value
     * @return {object}    the input parameter value for this variable or undefined
     */
    manageInputParameter(variableName, variableDef, defaultValue) {
      let paramValue = this.getInputParameterValue(variableName, variableDef);

      // Append fromUrl variable to the URL only if different from defaultValue
      if (variableDef.input === Constants.VariablePropertyInput.FROM_URL) {
        // paramValue might need to be coerce to the right type because the value
        // is a string if it's coming from the URL
        paramValue = AssignmentHelper.coerceType(paramValue, variableDef.type);

        // This call to get defValue can break in the case where we are dealing with an expression because the
        // expression evaluation (which passes the ko.isObservable() check in the Utils code, fails to evaluate
        // properly as the expression cannot be correctly evaluated in an early stage of constant/variable creation)
        // Example, when an interface constant is defined like so, it fails when called during createConstant
        // "interface": {
        //   "constants": {
        //     "qux": {
        //       "type": "string",
        //       "defaultValue": "{{ $constants.DEFAULT_VALUE }}",
        //       "input": "fromUrl"
        //     }
        //   }
        // }
        let defValue;
        try {
          defValue = Utils.resolveIfObservable(defaultValue);
        } catch (e) {
          const message = String(e);
          this.log.info('A defaultValue for variable/constant', variableName, 'cannot be obtained because of error',
            message);
        }

        if (paramValue !== defValue) {
          this.history.setUrlParameter(variableName, paramValue);
        }

        return paramValue;
      }

      return paramValue;
    }

    /**
     * Refresh the container and its parent with new input parameters.
     *
     * @param  {boolean} noReset a flag indicating if variable should not be updated to its default
     * value when the input parameter is not specified.
     */
    refreshInputParameters(noReset) {
      if (this.parent) {
        this.parent.refreshInputParameters(noReset);
      }
      if (this.scope) {
        this.scope.variablesDef.forEach((variable) => {
          if (variable.namespace === Constants.VariableNamespace.VARIABLES) {
            const input = variable.descriptor && variable.descriptor.input;
            const allDefs = this.getAllDefinitionOf('variables');
            const variableDef = allDefs[variable.name];
            if (input === Constants.VariablePropertyInput.FROM_CALLER
              || input === Constants.VariablePropertyInput.FROM_URL) {
              const inputParamValue = this.manageInputParameter(variable.name, variableDef, variable.defaultValue);

              // When the noReset flag is on, only update the variable if the value is defined.
              // This is so flow input vars are not updated when navigating to another page
              // without specifying them.
              if (noReset) {
                if (inputParamValue !== undefined) {
                  variable.setValue(inputParamValue);
                }
              } else {
                // If the input parameter is not defined, set the value to the initialValue
                // which take into account the value stored in history
                const value = this.scope.calculateInitialValue(
                  variable.name,
                  variable.namespace,
                  StateUtils.getType(variable.name, variableDef, this.scopeResolver),
                  variable.defaultValue,
                  inputParamValue,
                  variable.descriptor.persisted,
                );

                variable.setValue(value);
              }
            }
          }
        });
      }
    }

    /**
     * Refresh each history persisted variable in the container hierarchy with a potential new value
     * based on what is stored in browser history state.
     */
    refreshHistoryPersistedVariables() {
      const { parent } = this;
      // Stop recursing when reaching the top application container
      // or for switcher when reaching the root page
      if (parent && !parent.isNavigationBoundary()) {
        parent.refreshHistoryPersistedVariables();
      }

      if (this.scope) {
        this.scope.variablesDef.forEach((variable) => {
          if (variable.namespace === Constants.VariableNamespace.VARIABLES
            && variable.descriptor.persisted === Constants.VariablePropertyPersisted.HISTORY) {
            const allDefs = this.getAllDefinitionOf('variables');
            const variableDef = allDefs[variable.name];

            const value = this.scope.calculateInitialValue(
              variable.name,
              variable.namespace,
              StateUtils.getType(variable.name, variableDef, this.scopeResolver),
              variable.defaultValue,
              undefined,
              variable.descriptor.persisted,
            );

            if (value !== undefined) {
              variable.setValue(value);
            }
          }
        });
      }
    }

    /**
     * Looks up the extended container of the current container. If no extended container
     * is found, a dummy one is created.
     * @param extension current extension
     * @return extended container
     */
    getParentExtensionContainer(extension) {
      let extContainer = this.extensions[extension.id];
      if (!extContainer) {
        const path = this.application.extensionRegistry
          .getBasePathForContainer(this.path, /** @type {*} */ (this));
        extContainer = this.createExtension(extension, path);
        this.extensions[extension.id] = extContainer;
      }

      return extContainer;
    }

    /**
     * Creates an extension container.
     * @param extension current extension
     * @param path path to the container for which to create an extension container
     * @return extension container
     */
    createExtension(extension, path) {
      const Clazz = Object.getPrototypeOf(this).constructor.extensionClass;
      const extContainer = new (Clazz)(extension, path, this);
      extContainer.initDefault({});
      // it's a mock extension, so there is no module functions to load
      extContainer.loadFunctionsPromise = Promise.resolve();
      extContainer.expressionContext = new (extContainer.constructor.ContextType)(extContainer);

      return extContainer;
    }

    /**
     * Build the title that will be used for this page.
     * Walk up the flow hierarchy and gather all the title
     *
     * @param {String} [title] the base of the title
     * @return {String} the title
     */
    // eslint-disable-next-line no-unused-vars,class-methods-use-this
    buildTitle(title) {
      // No-op, for subclass to implement
      return '';
    }

    /**
     * true if the types defines the type
     * @param name
     * @return {*}
     */
    hasType(name) {
      // types defined in interface have precedence. There is an audit in DT to catch duplicates
      return this.definition.interface.types[name] || this.definition.types[name];
    }

    /**
     * true if the given event is defined in the container
     * @param name
     * @returns {*}
     */
    hasEvent(name) {
      // events defined in interface have precedence. There is an audit in DT to catch duplicates
      return this.definition.interface.events[name] || this.definition.events[name];
    }

    /**
     * true if the type is defined in the interface section of the current container.
     * @param name type name
     * @returns {*}
     */
    isInterfaceType(name) {
      return this.definition.interface.types[name];
    }

    /**
     * Returns type information for the given type. This will only return information on
     * structure types, collections, or special types (i.e. ServiceDataProvider/ArrayDataProvider).
     *
     * For these, the structure of the type is defined within the 'structure' property, which
     * itself could also contain types.
     *
     * Since this method is implemented in the container base class, 'types' can be defined in
     * application, flow or page.
     *
     * @param {String} name The name of the types
     * @return {Object} the type definition for a give 'type' in 'types' meta-data
     */
    getType(name) {
      const typeDef = this.hasType(name);

      if (!typeDef) {
        throw new TypeError(`Type "${name}" does not exist in `
          + `${this.className} ${this.fullPath || this.definition.id}`);
      }

      // legacy type definition
      // Note: If you have a legacy type definition as follow:
      // types: {
      //   foo: {
      //     type: "string"
      //   }
      // }
      // we will favor the new syntax over the legacy syntax so in the above example,
      // type foo will be resolved to an object with a string property called type.
      // We believe it is very unlikely to have a legacy type definition that is simply an
      // alias for a primitive type.
      if ((typeDef.type === 'object' || typeDef.type === 'array')
        && (typeDef.definition === '*' || Utils.isObject(typeDef.definition))) {
        return typeDef;
      }

      // make it consistent with type definition on a variable
      return {
        type: typeDef,
      };
    }

    /**
     * @param prefix
     * @param variableDefinitions
     * @returns {Array}
     */
    // eslint-disable-next-line class-methods-use-this,default-param-last
    getTypeReferencesByPrefix(prefix = '', variableDefinitions) {
      // pure function
      function recurse(obj, callback) {
        if (obj && typeof callback === 'function') {
          Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === 'string') {
              callback(key, obj[key]);
            } else if (typeof obj[key] === 'object') {
              recurse(obj[key], callback);
            }
          });
        }
      }

      const types = [];
      function getTypeNames(key, value) {
        if ((key === 'type' || key === 'definition') && value.startsWith(prefix)) {
          types.push(value.substring(prefix.length));
        }
      }

      recurse(variableDefinitions, getTypeNames);

      return types;
    }

    /**
     * Give a chance to the Security Provider to handle the error in order to possibly
     * redirect to a login page.
     * An error is thrown if the error is handled.
     *
     * @param  {Error} error the error to handle
     */
    callSecurityProvider(error) {
      const secProv = this.application.securityProvider;

      // Let the security provider handle the error. If it does there will be a
      // redirect, so re-throw the error so that the page is properly disposed,
      // otherwise swallow the exception.
      if (secProv && secProv.handleLoadError && secProv.handleLoadError(error, this.fullPath)) {
        throw error;
      }
    }

    /**
     * Walk the hierarchy of container until one has requiresAuthentication defined and returns
     * its value. The application default value is false.
     *
     * @return {Boolean} true if the authentication is required for this artifact, false otherwise
     */
    isAuthenticationRequired() {
      // security.access is initialized to non-null value in initDefault.
      const { requiresAuthentication } = this.definition.security.access;

      // if requiresAuthentication is defined, it takes precedence over the parent value
      if (requiresAuthentication !== undefined) {
        return requiresAuthentication;
      }

      // Default to true if not defined in app-flow.json (case when this.parent is falsy)
      return this.parent ? this.parent.isAuthenticationRequired() : true;
    }

    /**
     * Check if the access is allowed to this page or flow by calling the Security provider
     * Throw a 403 HttpError is the artifact require authentication and the user roles or
     * permissions don't satisfy the page security settings.
     */
    checkAccess() {
      return Promise.resolve().then(() => {
        // bypass security check when running in action chain test mode
        if (Utils.isActionChainTestMode()) {
          return;
        }

        // security.access is initialized to non-null value in initDefault.
        const accessInfo = this.definition.security.access;

        accessInfo.roles = accessInfo.roles || [];
        accessInfo.permissions = accessInfo.permissions || [];

        // Walk up the parent hierarchy to retrieve the requiresAuthentication flag
        accessInfo.requiresAuthentication = this.isAuthenticationRequired();

        if (accessInfo.requiresAuthentication === false) {
          // This is the case where the security definition is inconsistent, the
          // authentication is not required but a role or a permission is defined.
          if (accessInfo.roles.length > 0 || accessInfo.permissions.length > 0) {
            throw new Error(`Incorrect security configuration for ${this.className} ${this.fullPath}`);
          }

          // Authentication is not required, nothing else to do
          return;
        }

        const secProv = this.application.securityProvider;

        // This is the case where authentication is required but the Security Provider
        // is missing.
        if (!secProv) {
          throw new Error('A Security Provider is required to enforce access restriction'
            + ` for ${this.className} ${this.fullPath || this.definition.id}.`);
        }

        // For artifact level security, ask the security provider if the artifact access
        // info match this user. If not, throw a 403
        if (!secProv.isAccessAllowed(this.className, this.fullPath, accessInfo)) {
          // Throw a HTTP error with the 403 status "Forbidden" that will handled by the
          // Security Provider.
          throw new HttpError(403, null, `${this.fullPath} HTTP status: 403 Forbidden`);
        }
      });
    }

    /**
     * Register a layout.
     * This is to keep track of layout object used in this container so we can clean them on dispose
     * See usage in Layout.js constructor
     * @param {Layout} layout
     */
    registerLayout(layout) {
      this.layouts.push(layout);
    }

    /**
     * Dispose the scope and variable listeners. This can be called directly by the RuntimeManager at DT before
     * refreshing a page instance.
     */
    disposeScope() {
      if (this.scope) {
        this.scope.dispose();
        this.scope = null;
      }

      // clean up variables listeners
      this.variablesListeners.forEach((tracker) => {
        tracker.eventSource.remove(tracker.eventListener);
      });
      this.variablesListeners = [];
    }

    /**
     * Remove all references to other objects
     * For pages, it can be called from ojModule disconnect.
     */
    dispose() {
      this.loadMetadataPromise = null;
      this.loadDescriptorPromise = null;

      this.eventListeners = {};

      delete this.invokeComponentEvent;
      delete this.callListenerChainFunctionsInParallel;

      // Dispose of layouts in this container
      this.layouts.forEach((layout) => layout.dispose());
      this.layouts = [];

      this.loadFunctionsPromise = null;

      this.imports = {};

      // It is important to only nullify functions when the container is disconnected,
      // not when the dispose is called as a result of the parent flow being disposed.
      this.functions = null;

      if (this.availableContexts) {
        // Delete every property because components sometime holding onto those properties preventing
        // the parent object from getting garbage collected.
        // eslint-disable-next-line guard-for-in, no-restricted-syntax
        for (const prop in this.availableContexts) { delete this.availableContexts[prop]; }
        this.availableContexts = undefined;
      }

      if (this.scopeResolver) {
        // Something is sometime holding a reference to the scope resolver, so delete them individually
        // eslint-disable-next-line guard-for-in, no-restricted-syntax
        for (const prop in this.scopeResolver) { delete this.scopeResolver[prop]; }
        // Use delete because scopeResolver is readonly
        Reflect.deleteProperty(this, 'scopeResolver');
      }

      const re = this.getRulesEngine(false);
      if (re) {
        re.dispose(this);
      }

      // Propagate the dispose call to all extensions
      this.traverseExtensions('dispose');

      this.extensionsArray = [];
      this.extensions = {};
      this.modules = {};

      if (this.router) {
        this.router.dispose();
        this.router = null;
      }

      // Add a way to unregister the event when the container is deleted.
      this.unregisterEventArray.forEach((unregister) => unregister());
      this.unregisterEventArray = [];

      this.disposeScope();
      if (this.viewModelPromise) {
        const { viewModelPromise } = this;
        // Clear the cached promise synchronously. A disposed page instance can be reused before the
        // previous viewModelPromise settles; leaving it set would make Page.getViewModel() skip Page.run(false).
        this.viewModelPromise = null;

        viewModelPromise
          .then((viewModel) => {
            // Delete every property because components sometime holding onto those properties preventing
            // the parent object from getting garbage collected.
            // eslint-disable-next-line guard-for-in, no-param-reassign, no-restricted-syntax
            for (const prop in viewModel) { delete viewModel[prop]; }
          })
          .catch((error) => {
            this.log.error(error);
          });
      }

      if (this.webSockets) {
        Object.values(this.webSockets).forEach((ws) => ws.dispose());

        this.webSockets = {};
      }

      if (this.expressionContext) {
        this.expressionContext.dispose();
      }

      const { aiEventBus } = this.application;
      if (aiEventBus) {
        aiEventBus.removeAiResponseEventListeners(/** @type {*} */ (this));
        aiEventBus.removeAiRequestEventListener(/** @type {*} */ (this));
      }

      this.bundles = null;
      this.loadBundlesPromise = null;

      this.lifecycleState = Constants.ContainerState.DISPOSED;
    }

    /**
     * utility method to load a css file from url, absolute path or container relative path
     *
     * @param {Array} paths the array of paths in the definition
     * @return {Promise}
     * @private
     */
    importCss(paths) {
      return Promise.resolve()
        .then(() => {
          if (!Array.isArray(paths) || paths.length === 0) {
            return undefined;
          }
          // package and extension have their onw implementation of resolveCssPaths
          const allPaths = this.resolveCssPaths(paths);

          const loadPromises = [];
          if (allPaths.urlPaths.length > 0) {
            // Complete URL are loaded directly
            loadPromises.push(Utils.getResources(allPaths.urlPaths));
          }

          if (allPaths.cssPaths.length > 0) {
            // local css needs their relative reference to be adjusted
            loadPromises.push(Container.injectCss(allPaths.cssPaths, allPaths.cssUrls));
          }

          return Promise.all(loadPromises);
        })
        .catch((reason) => {
          logger.error('Unable to load css:', reason);
          // swallow errors, just report them
        });
    }

    /**
     * Traverse the list of path from the importsCss section of the metadata and build
     * and object with 2 arrays. One array of full URL to CSS and one array of relative CSS.
     * @param  {Array<String>} paths
     * @return {Object}        Object with 3 arrays cssPaths, cssUrls and urlPaths
     */
    resolveCssPaths(paths = []) {
      /** @type {{cssPaths: string[], cssUrls: string[], urlPaths: string[]}} */
      const results = {
        cssPaths: [], // path to load the potentially bundled css using require
        cssUrls: [], // complete url with protocol to the location of the css
        urlPaths: [], // complete URL with protocol, nothing is done to the css
      };

      paths.forEach((path) => {
        let cssPath = path.trim();
        const uri = URI.parse(cssPath);

        // local url
        if (!uri.protocol) {
          // absolute url
          if (cssPath[0] === '/') {
            cssPath = cssPath.substring(1);
          } else { // relative path
            cssPath = `${this.getResourceFolder()}${cssPath}`;
          }

          // Use require to retrieve the full URL, so relative path become full path with
          // application base URL and path for extension (starting with vx/extId/...) get
          // resolve to the full URL on CDN
          // From: vx/extId/ui/self/applications/appUi1/resources/css/app.css
          // to:   https://some-path/.../extId/ui/self/applications/appUi1//resources/css/app.css
          const newCssPath = requirejs.toUrl(cssPath);

          // Remove the file name from the path and store it in an array for later use
          results.cssUrls.push(newCssPath.substring(0, newCssPath.lastIndexOf('/') + 1));

          // Load the css using the text plugins so we can modify the content
          // When using require css plugin, require inject the content in the DOM instead of retrieving the content
          results.cssPaths.push(cssPath);
        } else {
          // Loads the url path using the css plugins since relative paths inside the css
          // are relative to the css location.
          results.urlPaths.push(`css!${cssPath}`);
        }
      });

      return results;
    }

    /**
     * Given a set of path to css files, load each css as a text file and inject
     * the combine content in the DOM using a <style> tags.
     * The css file is fetched from DT resources if the app is running in the page designer,
     * whereas for all other cases text plugins are used to load the css - need to verify this.
     * This routine also replace each relative paths in the css with an absolute path.
     * @param  {Array<String>} cssPaths array of paths to css
     * @param  {Array<String>} cssUrls array of URL to css location
     * @return {Promise}       a Promise that resolve when all css are loaded
     */
    static injectCss(cssPaths, cssUrls = []) {
      return Utils.getRuntimeEnvironment().then((runtimeEnvironment) => {
        Promise.all(cssPaths.map((cssPath) => runtimeEnvironment.getTextResource(cssPath)))
        // Aggregate content of all css to import, fix the path and inject the content in DOM
          .then((results) => {
            // Build one css with the content of each css entry in the import
            let allCssContent = '';

            // Traverse all the CSS entry and replace each references (url and @imports)
            results.forEach((css, index) => {
              if (css) {
                allCssContent += Utils.replacePathInCSS(css, (path) => {
                  let newPath = path;

                  const uri = URI.parse(path);
                  // only replace the path when not a full URL
                  if (!uri.protocol) {
                    // Combine the css folder calculated earlier with the path the resource
                    // in the css to build the absolute path
                    newPath = URI(`${cssUrls[index]}${path}`).normalizePath().toString();
                  }
                  return newPath;
                });
              }
            });

            // then inject the css in the head section of index.html
            Utils.injectStyleTag(allCssContent);
          });
      });
    }

    /**
     * used by event propagation; when trying to start the event, we typically need to know the 'lowest'
     * container in the 'tree' (a.k.a. leaf); we start from there, and bubble up.
     * But this may be different depending on the container type; for example, Layout doesn't participate the same way.
     *
     * Usually a Page, but can be a Flow if we're processing vb system events before the Page is created.
     *
     * @returns {Container|Page}
     */
    getLeafContainer() {
      return RouterUtils.getCurrentPage(this.rootRouter, true);
    }

    /**
     * Use by extension container and App UI container to adjust the path of a local resource.
     * @param  {String} path the import path
     * @return {String}      the adjusted path
     */
    // eslint-disable-next-line class-methods-use-this
    adjustImportPath(path) {
      // There is nothing to adjust to the path for a base container
      return path;
    }

    /**
     * Utility method to load a module, and log errors
     *
     * @param {Object} modules can be modules that are either components or JS modules that use require path, or can
     * be JS modules that use a scheme to locate the resource in current or another extension.
     * @param {Object} options
     * @property {boolean} options.storeImport true when the module should be stored in this.imports
     * @property {string} options.type - module or component (default)
     * @param {boolean} initializeFunctionProviders true when a manifest refresh reset provider initialization
     * @return {Promise|undefined} a promise to load module if there are module to import
     * @private
     */
    importModules(modules, options = { type: 'component' }, initializeFunctionProviders = false) {
      const allPathsPromises = [];
      const keys = [];
      const $funcsInfo = {};
      const isWebapp = !Utils.isHostApplication();
      const moduleKeys = Object.keys(modules);
      const failedFunctionImportKeys = new Set();
      let functionProviderRecoveryAttempted = initializeFunctionProviders;

      const logFunctionProviderFailure = (key, path, error) => {
        const modKP = `${key}: (path: ${path})`;
        this.log.error('Cannot load module', modKP, 'defined in:', this.path,
          ', because function provider initialization or path resolution failed:', error);
      };

      const getImportPath = (key) => {
        const importDef = modules[key];
        let path;

        if (Utils.isObject(importDef)) {
          path = importDef.path;
          path = (typeof path === 'string') && path.trim();
        }
        return path;
      };

      const getFunctionProviderInfo = (path) => {
        const { prefix, main, suffix } = Utils.parseQualifiedIdentifier(path);
        const extId = (prefix === Constants.ExtensionFolders.SELF)
          ? this.extension.id : (prefix || this.extension.id);
        let { extension } = this;
        let providerType = 'extension';
        let registry = (this.extension && this.extension.registry) || this.application.extensionRegistry;
        let isExternalProvider = false;

        if (prefix === Constants.GLOBAL_PREFIX) {
          providerType = Constants.GLOBAL_PREFIX;
          extension = this.application.extension;
          registry = (extension && extension.registry) || registry;
        } else if (isWebapp && prefix === Constants.APPLICATION_PREFIX) {
          // application:$functions is a web-app alias for the application extension, not an extension id.
          extension = this.application.extension;
          registry = (extension && extension.registry) || registry;
        } else if (extId !== this.extensionId) {
          extension = registry && registry.getExtensionById(extId);
          isExternalProvider = true;
        }

        return {
          prefix,
          main,
          suffix,
          extId,
          extension,
          providerType,
          registry,
          isExternalProvider,
        };
      };

      const isFunctionImportPath = (path) => path && !FUNCTIONS_FOLDER_RE.test(path)
        && (EXTENSIONID_FUNCTIONS_RE.test(path) || GLOBAL_FUNCTIONS_FOLDER_RE.test(path));

      const manifestRegistries = new Set();

      function addManifestRegistry(registry) {
        if (registry) {
          manifestRegistries.add(registry);
        }
      }

      // Plain module imports can depend on RequireJS bundle/path metadata from the active registry even when no
      // $functions provider import is involved.
      const activeManifestRegistry = (this.extension && this.extension.registry)
        || (this.application && this.application.extensionRegistry);
      const ownerExtension = this.extension;
      const ownerExtensionId = this.extensionId;
      const ownerPath = this.path;
      const ownerLog = this.log;
      addManifestRegistry(activeManifestRegistry);
      moduleKeys.forEach((key) => {
        const path = getImportPath(key);
        if (isFunctionImportPath(path)) {
          const { registry } = getFunctionProviderInfo(path);
          addManifestRegistry(registry);
        }
      });

      const getManifestGeneration = (registry) => (
        typeof registry.manifestGeneration === 'number' ? registry.manifestGeneration : 0
      );

      const manifestRegistryArray = Array.from(manifestRegistries);
      function getManifestGenerations() {
        const generations = new Map();
        manifestRegistryArray.forEach((registry) => {
          generations.set(registry, getManifestGeneration(registry));
        });
        return generations;
      }
      const whenManifestsStable = () => Promise.all(manifestRegistryArray.map((registry) => (
        typeof registry.whenManifestStable === 'function'
          ? registry.whenManifestStable()
          : Promise.resolve(getManifestGeneration(registry))
      ))).then(() => getManifestGenerations());

      const manifestGenerationChanged = (generations) => Array.from(generations)
        .some(([registry, generation]) => getManifestGeneration(registry) !== generation);
      const initialManifestGenerations = getManifestGenerations();

      const isFunctionProviderVisible = (registry, extId) => {
        if (!registry) {
          return false;
        }
        if (typeof registry.isDependent === 'function') {
          return registry.isDependent(this.extensionId, extId);
        }

        // Some compatible registries and unit-test doubles predate isDependent(). Fall back to the direct dependency
        // contract that importModules enforced before partial-manifest recovery was introduced.
        const refreshedOwner = registry.getExtensionById(this.extensionId);
        const owner = refreshedOwner && refreshedOwner.id === this.extensionId ? refreshedOwner : this.extension;
        return !!(owner && owner.dependencies && owner.dependencies[extId]);
      };

      const logFunctionProviderVisibilityFailure = (key, path, extId) => {
        const modKP = `${key}: (path: ${path})`;
        this.log.error('Cannot load module', modKP, 'defined in:', this.path, ', because', extId,
          'does not appear in the list of dependencies for extension', this.extensionId);
      };

      const recoverFunctionProvider = ({
        key, path, extId, registry,
      }) => {
        functionProviderRecoveryAttempted = true;
        return Promise.resolve().then(() => registry.loadFullManifest(extId)).catch((error) => {
          failedFunctionImportKeys.add(key);
          const modKP = `${key}: (path: ${path})`;
          this.log.error('Cannot load module', modKP, 'defined in:', this.path,
            ', because full manifest recovery for', extId, 'failed:', error);
        });
      };

      const recoverMissingFunctionProviders = () => {
        const recoveryPromises = [];

        moduleKeys.forEach((key) => {
          const path = getImportPath(key);
          if (!path || FUNCTIONS_FOLDER_RE.test(path)
            || !(EXTENSIONID_FUNCTIONS_RE.test(path) || GLOBAL_FUNCTIONS_FOLDER_RE.test(path))) {
            return;
          }

          const {
            extension, extId, registry, isExternalProvider,
          } = getFunctionProviderInfo(path);
          if (!isExternalProvider || extension || !registry || typeof registry.loadFullManifest !== 'function') {
            return;
          }
          if (!isFunctionProviderVisible(registry, extId)) {
            // Partial-digest dependency metadata can be incomplete. Log the dependency error, but let full-manifest
            // recovery make the authoritative visibility decision before provider initialization.
            this.log.error('Extension', this.extensionId, 'references function provider extension', extId,
              'from module', key, 'with path', path, 'without declaring it as a dependency.');
          }

          recoveryPromises.push(recoverFunctionProvider({
            key, path, extId, registry,
          }));
        });

        return Promise.all(recoveryPromises);
      };

      /**
       * @param {string} resourceFilePath extension-relative resource path
       * @returns {boolean} true when the resource path identifies a packaged extension module
       */
      function isRecoverableExtensionModuleResourcePath(resourceFilePath) {
        // Full-manifest recovery is only for packaged module resources. Other vx/ URLs, such as dynamic layout
        // metadata probes, can be legitimate runtime misses and should fall through to normal module loading.
        return resourceFilePath.startsWith('resources/') || resourceFilePath.includes('/resources/');
      }

      /**
       * @param {string} path module path after container-relative path adjustment
       * @returns {{extId: string, filePaths: string[]}|undefined} extension resource metadata
       */
      function getExtensionModuleResourceInfo(path) {
        const pluginIndex = path.indexOf('!');
        const resourcePath = pluginIndex >= 0 ? path.slice(pluginIndex + 1) : path;
        if (!resourcePath.startsWith(Constants.EXTENSION_PATH)) {
          return undefined;
        }

        const extensionResourcePath = resourcePath.slice(Constants.EXTENSION_PATH.length);
        const separatorIndex = extensionResourcePath.indexOf('/');
        if (separatorIndex <= 0 || separatorIndex === extensionResourcePath.length - 1) {
          return undefined;
        }

        const extId = extensionResourcePath.slice(0, separatorIndex);
        const resourceFilePath = extensionResourcePath.slice(separatorIndex + 1);
        if (!isRecoverableExtensionModuleResourcePath(resourceFilePath)) {
          return undefined;
        }
        const filePaths = [resourceFilePath];
        const resourceFileName = resourceFilePath.split('/').pop();
        if (resourceFileName && resourceFileName.indexOf('.') < 0) {
          filePaths.push(`${resourceFilePath}.js`);
        }
        return { extId, filePaths };
      }

      /**
       * @param {Object} registry extension registry
       * @returns {boolean} true when the registry can still be upgraded from partial to full metadata
       */
      function canRecoverModuleResources(registry) {
        return registry && typeof registry.loadFullManifest === 'function'
          && (typeof registry.isPartialDigest !== 'boolean' || registry.isPartialDigest);
      }

      /**
       * @param {string} extId parsed extension resource id
       * @returns {boolean} true when the id is a module path pseudo id rather than a recoverable extension id
       */
      function isPseudoExtensionModuleResource(extId) {
        return extId === Constants.ExtensionFolders.SELF || extId === Constants.ExtensionFolders.BASE
          || extId === Constants.ExtensionNamespaces.BASE;
      }

      /**
       * @param {Object} registry extension registry
       * @param {string} extId extension id
       * @returns {boolean} true when dependency metadata makes the module provider visible to the owner
       */
      function isModuleResourceProviderVisible(registry, extId) {
        if (!registry) {
          return false;
        }
        if (extId === ownerExtensionId || isPseudoExtensionModuleResource(extId)) {
          return true;
        }
        if (typeof registry.isDependent === 'function') {
          return registry.isDependent(ownerExtensionId, extId);
        }

        // Some compatible registries and unit-test doubles predate isDependent(). Fall back to the direct dependency
        // contract that importModules enforced before partial-manifest recovery was introduced.
        const refreshedOwner = registry.getExtensionById && registry.getExtensionById(ownerExtensionId);
        const owner = refreshedOwner && refreshedOwner.id === ownerExtensionId ? refreshedOwner : ownerExtension;
        return !!(owner && owner.dependencies && owner.dependencies[extId]);
      }

      /**
       * @param {Object} registry extension registry
       * @param {{extId: string}} resourceInfo extension resource metadata
       * @returns {boolean} true when a same-extension module import belongs to an owner absent from the active registry
       */
      function isInactiveOwnerModuleResource(registry, resourceInfo) {
        return resourceInfo.extId === ownerExtensionId && registry && typeof registry.getExtensionById === 'function'
          && !registry.getExtensionById(resourceInfo.extId);
      }

      /**
       * @param {Object} registry extension registry
       * @param {string} extId extension id
       * @param {string[]} filePaths possible extension-relative file paths
       * @returns {boolean} true when the active manifest already exposes the module resource
       */
      function registryHasModuleResource(registry, extId, filePaths) {
        const extension = registry && registry.getExtensionById && registry.getExtensionById(extId);
        if (extension && typeof extension.hasFile === 'function'
          && filePaths.some((filePath) => extension.hasFile(filePath))) {
          return true;
        }
        if (extension && Array.isArray(extension.files)
          && filePaths.some((filePath) => extension.files.includes(filePath))) {
          return true;
        }
        if (registry && typeof registry.getExtensionsWithFile === 'function') {
          return filePaths.some((filePath) => {
            const extensions = registry.getExtensionsWithFile(filePath) || [];
            return extensions.some((fileExtension) => fileExtension.id === extId);
          });
        }
        return false;
      }

      /**
       * @param {Object} registry extension registry
       * @param {{extId: string, filePaths: string[]}} resourceInfo extension resource metadata
       * @param {string} adjustedPath adjusted module path that will be loaded
       * @returns {boolean} true when current metadata can already load the module resource
       */
      function isKnownModuleResource(registry, resourceInfo, adjustedPath) {
        if (registryHasModuleResource(registry, resourceInfo.extId, resourceInfo.filePaths)) {
          return true;
        }

        // Partial App UI digests can apply the RequireJS bundle entry that makes a module loadable even when the
        // runtime file index is not enough to prove the resource exists. Do not recover in that already-bundled case.
        return ConfigLoader.isResourceBundled(adjustedPath);
      }

      /**
       * Recover missing extension bundle metadata for plain module imports before RequireJS loads them directly.
       *
       * This closes the case where the partial digest never included the bundled resource, so merely waiting for
       * in-flight manifest work cannot install the RequireJS bundle mapping needed for the module id.
       * @param {Array<string|symbol>} paths adjusted module paths that will be loaded
       * @param {string[]} pathKeys keys corresponding to the adjusted module paths
       * @returns {Promise<Array<void|boolean>>} promise resolving after all recovery attempts settle
       */
      function recoverMissingModuleResources(paths, pathKeys) {
        if (!canRecoverModuleResources(activeManifestRegistry)) {
          return Promise.resolve([]);
        }

        const recoveringExtensions = new Set();
        const recoveryPromises = [];
        pathKeys.forEach((key, index) => {
          const adjustedPath = paths[index];
          if ($funcsInfo[key] || typeof adjustedPath !== 'string') {
            return;
          }

          const resourceInfo = getExtensionModuleResourceInfo(adjustedPath);
          if (!resourceInfo || isPseudoExtensionModuleResource(resourceInfo.extId)
            || isInactiveOwnerModuleResource(activeManifestRegistry, resourceInfo)
            || isKnownModuleResource(activeManifestRegistry, resourceInfo, adjustedPath)
            || recoveringExtensions.has(resourceInfo.extId)) {
            return;
          }

          if (!isModuleResourceProviderVisible(activeManifestRegistry, resourceInfo.extId)) {
            ownerLog.error('Extension', ownerExtensionId, 'references module resource extension', resourceInfo.extId,
              'from module', key, 'with path', getImportPath(key), 'without declaring it as a dependency.');
          }

          recoveringExtensions.add(resourceInfo.extId);
          recoveryPromises.push(Promise.resolve(activeManifestRegistry.loadFullManifest(resourceInfo.extId))
            .catch((error) => {
              const modKP = `${key}: (path: ${getImportPath(key)})`;
              ownerLog.error('Cannot load module', modKP, 'defined in:', ownerPath,
                ', because full manifest recovery for', resourceInfo.extId, 'failed:', error);
            }));
        });

        return Promise.all(recoveryPromises);
      }

      const importModulesFromManifest = (manifestGenerations) => {
        moduleKeys.forEach((key) => {
          let path = getImportPath(key);

          if (!path) {
            this.log.error('Unable to load', options.type, key, 'in', this.className, this.fullPath,
              'because the path is missing.');
            return;
          }

          if (FUNCTIONS_FOLDER_RE.test(path)) {
            this.log.error('Unable to load', path,
              '. Direct $functions imports are not supported.',
              `Use ${isWebapp ? '\'application:\' or \'global:\'' : '\'self:\' or \'<extension-id>:\''}`,
              'prefixed function imports instead.');
            return;
          }

          if (failedFunctionImportKeys.has(key)) {
            return;
          }

          if (EXTENSIONID_FUNCTIONS_RE.test(path) || GLOBAL_FUNCTIONS_FOLDER_RE.test(path)) {
            // if path includes $functions then it needs other resources to be loaded, before locating file path
            const {
              prefix, main, suffix, extId, extension, providerType, registry, isExternalProvider,
            } = getFunctionProviderInfo(path);

            // The reference 'application' should be used for web applications.
            // Extensions support 'self' and extension ids.
            if (main === '$functions' && ((prefix === Constants.APPLICATION_PREFIX && !isWebapp)
              || (![Constants.APPLICATION_PREFIX, Constants.GLOBAL_PREFIX].includes(prefix) && isWebapp))) {
              const appType = isWebapp ? 'Web / Visual Applications' : 'Extensions';
              const referenceType = isWebapp ? 'application: or global:' : 'self: or <extension-id>:';
              this.log.warn(`'${prefix}:' prefix is not allowed in the path locator for loading global functions`,
                `in '${appType}'. Please use prefix '${referenceType}' instead.`);
            }

            const getInitializedProviderFunctionPath = (providerExtension) => Promise.resolve()
              // Global imports initialize each candidate while walking the refreshed provider list.
              .then(() => (providerType === Constants.GLOBAL_PREFIX
                || (!isExternalProvider && !functionProviderRecoveryAttempted)
                || typeof providerExtension.init !== 'function' ? undefined : providerExtension.init()))
              .then(() => {
                $funcsInfo[key.trim()] = {
                  extension: providerExtension,
                  alias: suffix,
                  providerType,
                  path,
                };
                return this.getModulesFunctionPath({
                  ext: providerExtension,
                  resourceType: main,
                  alias: suffix,
                  path,
                  providerType,
                  initializeGlobalProviders: functionProviderRecoveryAttempted,
                });
              })
              .catch((error) => {
                logFunctionProviderFailure(key, path, error);
                return SKIP_MODULE_IMPORT;
              });
            if (isExternalProvider) {
              // Recovery refreshes the owner metadata too. Re-check visibility before initializing or importing the
              // provider so a stale partial dependency cannot grant access from the full manifest.
              if (!isFunctionProviderVisible(registry, extId)) {
                logFunctionProviderVisibilityFailure(key, path, extId);
                return;
              }
              const providerExtension = registry && registry.getExtensionById(extId);
              if (!providerExtension) {
                const modKP = `${key}: (path: ${path})`;
                this.log.error('Cannot load module', modKP, 'defined in:', this.path, ', because', extId,
                  'is not available in the extension registry.');
                return;
              }

              allPathsPromises.push(getInitializedProviderFunctionPath(providerExtension));
              keys.push(key.trim());
              return;
            }

            allPathsPromises.push(getInitializedProviderFunctionPath(extension));
          } else {
            // If path starts with ./, it means that it is relative to the current container's
            // resourceFolder location.
            if (path.startsWith('./')) {
              path = `${this.getResourceFolder()}${path}`;
            }
            // most paths are straight forward - wysiwyg
            allPathsPromises.push(Promise.resolve(this.adjustImportPath(path)));
          }
          keys.push(key.trim());
        });

        return Promise.all(allPathsPromises).then((paths) => recoverMissingModuleResources(paths, keys)
          .then(() => whenManifestsStable())
          .then(() => {
            if (manifestGenerationChanged(manifestGenerations)) {
              return this.importModules(modules, options, true);
            }

            const allPaths = paths.filter((path) => typeof path === 'string');
            const allKeys = keys.filter((_key, index) => typeof paths[index] === 'string');
            if (allPaths.length > 0) {
              // Load all components / modules in one getResources call
              return Promise.allSettled(
                allPaths.map((path) => this.application.runtimeEnvironment.getModuleResource(path)))
                .then((results) => whenManifestsStable().then(() => {
                  if (manifestGenerationChanged(manifestGenerations)) {
                    return this.importModules(modules, options, true);
                  }

                  if (options.storeImport) {
                    for (let index = 0; index < results.length; index += 1) {
                      const modName = allKeys[index];
                      const result = results[index];

                      if (result.status === 'rejected') {
                        logger.error(modName, 'failed to load for the following reason:\n', result.reason, this.id);
                      } else {
                        const modJS = result.value;
                        if (modJS) {
                          let moduleValue = modJS;
                          this.imports[modName] = moduleValue;
                          // every non $functions module should be part of the new modules
                          this.modules[modName] = moduleValue;
                          if ($funcsInfo && $funcsInfo[modName]) {
                            // function modules will need to be Proxy-ed to use to aliased function names
                            const info = $funcsInfo[modName];
                            const metadataEntry = info.providerType === Constants.GLOBAL_PREFIX
                              ? Container.getGlobalFunctionMetadataEntry(this.application.extension, info.alias)
                              : {
                                providerExtension: info.extension,
                                functionMetadata: info.extension
                                  && info.extension.extensionResources.functionsMetadata
                                  && info.extension.extensionResources.functionsMetadata[info.alias],
                              };
                            const fileDef = metadataEntry && metadataEntry.functionMetadata;
                            if (!fileDef) {
                              logger.error('Unable to locate function', info.alias, 'in path', info.path);
                              return undefined;
                            }
                            const resolvedProvider = info.providerType === Constants.GLOBAL_PREFIX
                              ? metadataEntry.providerExtension
                              : info.extension;
                            const nonSelf = resolvedProvider
                              ? this.extension.id !== resolvedProvider.id
                              : this.extension.id !== Constants.ExtensionFolders.BASE;
                            const funcsDefAlt = {};

                            // Rules for setting up proxy over functions, for each file are based on where the imports
                            //  is done - particularly for the case where a function is called using $modules set up
                            //  by another extension,
                            // (1) function marked referenceable: 'extension' must also have 'extension' set on parent
                            // (file).
                            // (2) function marked referenceable: 'self', is excluded from being proxied, if the
                            // parent is set to "extension", and function is called from a different extension. Same can
                            // be called from current extension
                            // (3) function marked referenceable: 'extension' is excluded when parent is set to 'self',
                            // and function is called from $modules of another extension. It is allowed when called from
                            // $modules from same extension
                            Object.keys((fileDef.functions || {}))
                              .forEach((funcy) => {
                                const funcyDef = fileDef.functions[funcy] || {};
                                funcsDefAlt[funcy] = { methodName: funcy };
                                const effectiveReferenceable = funcyDef.referenceable || fileDef.referenceable;
                                funcsDefAlt[funcy].referenceable = effectiveReferenceable;

                                const providerId = info.providerType === Constants.GLOBAL_PREFIX
                                  ? ((metadataEntry.providerExtension && metadataEntry.providerExtension.id)
                                    || Constants.GLOBAL_PREFIX)
                                  : info.extension.id;
                                const excludeMsg = `The function ${funcy} cannot be called from the `
                                  + `extension ${this.extensionId}, as access to xxx is restricted by the `
                                  + `extension ${providerId} where it is defined.`;

                                // Webapps support 'self' at app scope and 'global' when importing host application
                                // global functions into App UIs and extensions.
                                if (isWebapp && ((funcyDef.referenceable
                                  && ![Constants.ExtensionReferenceable.SELF,
                                    Constants.ExtensionReferenceable.GLOBAL].includes(funcyDef.referenceable))
                                  || (fileDef.referenceable
                                  && ![Constants.ExtensionReferenceable.SELF,
                                    Constants.ExtensionReferenceable.GLOBAL].includes(fileDef.referenceable)))) {
                                  const referenceableType = fileDef.referenceable !== 'self' ? fileDef.referenceable
                                    : funcyDef.referenceable;
                                  this.log.warn(`The referenceable '${referenceableType}', defined in functions.json`,
                                    `for the file '${fileDef.fileName}', is not a valid referenceable for loading`,
                                    'global functions in Web / Visual Applications.',
                                    'Please use \'self\' or \'global\' instead.');
                                }

                                if (funcyDef.referenceable === Constants.ExtensionReferenceable.EXTENSION
                                  && fileDef.referenceable === Constants.ExtensionReferenceable.SELF && nonSelf) {
                                  // exclude (3)
                                  funcsDefAlt[funcy].excludeAccess = excludeMsg.replace('xxx', `the module ${modName}`);
                                }
                                if (funcyDef.referenceable === Constants.ExtensionReferenceable.SELF
                                  && fileDef.referenceable === Constants.ExtensionReferenceable.EXTENSION && nonSelf) {
                                  // exclude (2)
                                  funcsDefAlt[funcy].excludeAccess = excludeMsg.replace('xxx', 'this function');
                                }
                                if (nonSelf && info.providerType === Constants.GLOBAL_PREFIX
                                  && effectiveReferenceable !== Constants.ExtensionReferenceable.GLOBAL) {
                                  funcsDefAlt[funcy].excludeAccess = excludeMsg.replace('xxx', `the module ${modName}`);
                                }
                              });
                            moduleValue = PROXY_OVER_FUNC_METHOD(modJS,
                              funcsDefAlt, info.alias,
                              info.providerType === Constants.GLOBAL_PREFIX
                                ? Constants.GLOBAL_PREFIX : info.extension.id);
                            this.imports[modName] = moduleValue;
                            this.modules[modName] = moduleValue;
                          }
                        }
                      }
                    }
                  }
                  return undefined;
                }));
            }

            return undefined;
          }));
      };

      // Start provider recovery before waiting for manifest stability. loadFullManifest() sends the recovery-start
      // signal that lets queued partial-digest work stand down, so waiting first can block the recovery that would
      // make the manifest stable.
      return recoverMissingFunctionProviders()
        // Capture the generation after recovery initiated by this invocation has completed. Later changes came from
        // another manifest consumer and require this import to be resolved again from refreshed metadata.
        .then(() => whenManifestsStable())
        .then((manifestGenerations) => {
          if (manifestGenerationChanged(initialManifestGenerations)) {
            functionProviderRecoveryAttempted = true;
          }
          return importModulesFromManifest(manifestGenerations);
        });
    }

    /**
     * Retrieves the path to a $functions file JS.
     * A container can include a path scheme of the form - {extId}:$functions/{alias}
     * Example:
     *  - "extA:$functions/foo" where 'foo' is an alias for the JS file declared in functions.json defined under the
     *  extension extA, and the file defined under 'extA/ui/self/resources/functions/<some-sub-folder/fileFoo>'.
     *
     *  - "self:$functions/bar", where self refers to current extension
     *  - "$functions/foo", is not supported as it could mean - load function defined under current appUI. This is
     *    not supported.
     *  - Supported:
     *    - global:$functions/lucy - could refer to unified app resource functions
     *    - $functions/{alias} - could refer to resources under current appUi
     *    - <extId | self>/<appUi>:$functions/{alias} - could refer to resource defined in different appUi
     *
     * Functions not defined in functions.json will not be loaded because there is metadata that determines
     * its access from the current and other downstream extensions.
     *
     * @param  {object} options the import path
     * @property {Extension} options.extension extension for which the path to function JS needs to be determined
     * @property {string} options.resourceType types of resource. defaults to $functions
     * @property {string} options.alias of specific function file to load
     * @property {string} options.path configured path on imports
     * @property {boolean} options.initializeGlobalProviders initialize refreshed global function providers
     * @return {Promise<string|*>} resolves with the functions file path, or undefined if it can't be found
     * @private
     */
    getModulesFunctionPath({
      ext = this.extension,
      resourceType = '$functions',
      alias = '',
      path = '',
      providerType = 'extension',
      initializeGlobalProviders = false,
    }) {
      return Promise.resolve().then(() => {
        const providerExtension = ext;

        if (resourceType === '$functions' && providerExtension) {
          const metadataFolder = Container.getFunctionMetadataFolder(providerExtension, providerType);
          const metadataFilePath = `${metadataFolder}/functions.json`;
          const metadataErr = `A functions metadata ${metadataFilePath} was not found. It is required to load`
            + ` the module specified by ${path}. Check your configuration`;

          // Global imports may be satisfied by the app itself or one of its required extensions.
          if (providerType === Constants.GLOBAL_PREFIX) {
            return this.resolveGlobalFunctionPathFromProviders({
              alias,
              importPath: path,
              application: this.application,
              metadataOwnerExtension: providerExtension,
              initializeProviders: initializeGlobalProviders,
            });
          }

          // Non-global imports still resolve from a single owning extension.
          if (!Container.validateFunctionManifestPath(providerExtension, metadataFilePath, path, metadataErr)) {
            return undefined;
          }

          return this.getFunctionsMetadata(providerExtension, metadataFolder, providerType)
            .then((functionFiles) => {
              const functionMetadata = functionFiles[alias];
              if (!functionMetadata) {
                logger.error('Unable to locate function', alias, 'in path', path);
                return undefined;
              }

              // Cross-extension imports only allow functions explicitly exposed to dependent extensions.
              if (this.extension.id !== providerExtension.id
                && functionMetadata.referenceable !== Constants.ExtensionReferenceable.EXTENSION) {
                logger.error('Unable to access the module with path', path, 'from current extension',
                  this.extension.id);
                return undefined;
              }

              return Container.validateFunctionManifestPath(
                providerExtension,
                functionMetadata.relativePath,
                path,
              ) && functionMetadata.path;
            });
        }
        return path;
      });
    }

    /**
     * Returns the folder containing functions metadata for the given provider.
     * Global functions are defined at the application resources level while extension functions
     * remain under the extension's `ui/self/resources/functions` location.
     *
     * @param {Extension} extension
     * @param {string} providerType
     * @returns {string}
     * @private
     */
    static getFunctionMetadataFolder(extension, providerType = 'extension') {
      if (providerType === Constants.GLOBAL_PREFIX) {
        return 'resources/functions';
      }

      return `${(extension.id !== Constants.ExtensionFolders.BASE)
        ? `${Constants.DefaultPaths.UI}${Constants.ExtensionFolders.SELF}/`
        : ''}resources/functions`;
    }

    /**
     * Verifies that a metadata or function file is present in the provider's manifest.
     *
     * @param {Extension} providerExtension
     * @param {string} manifestPath
     * @param {string} importPath
     * @param {string} [message]
     * @param {boolean} [silent=false]
     * @returns {string|undefined}
     * @private
     */
    static validateFunctionManifestPath(providerExtension, manifestPath, importPath, message, silent = false) {
      try {
        providerExtension.fileExists(manifestPath);
      // eslint-disable-next-line no-unused-vars
      } catch (e) {
        if (!silent) {
          const errorMessage = message
            || `Function defined in ${importPath} is not found in the extension registry using, ${manifestPath}.`
              + 'Check your configuration.';
          logger.error(errorMessage);
        }
        return undefined;
      }
      return manifestPath;
    }

    /**
     * Builds the ordered list of candidate providers for a `global:$functions/...` import.
     * We start with the application itself and then walk its required extensions.
     *
     * Global imports can come from the host web app itself or from registry-backed extensions that
     * the app has loaded. The synthetic application extension container only represents the host app
     * resources, so dependency traversal must start from the application's real extension containers.
     *
     * @param {Application} application
     * @returns {Array<Extension>}
     * @private
     */
    static getGlobalFunctionProviderExtensions(application) {
      const providerExtensions = [];
      const seenProviderIds = new Set();
      const pendingProviders = [];

      const enqueueProvider = (providerExtension) => {
        if (providerExtension) {
          pendingProviders.push(providerExtension);
        }
      };

      // The application itself stores the host provider as the raw extension object, while
      // application.extensionsArray stores extension containers that wrap their raw extension.
      enqueueProvider(application && application.extension);
      ((application && application.extensionsArray) || []).forEach((extensionContainer) => {
        enqueueProvider(extensionContainer && extensionContainer.extension);
      });

      while (pendingProviders.length > 0) {
        const candidateExtension = pendingProviders.shift();
        if (!candidateExtension) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const candidateId = candidateExtension.id || candidateExtension.baseUrl || 'base';
        if (seenProviderIds.has(candidateId)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        seenProviderIds.add(candidateId);
        providerExtensions.push(candidateExtension);

        if (typeof candidateExtension.getRequiredExtensions === 'function') {
          pendingProviders.push(...candidateExtension.getRequiredExtensions());
        }
      }

      return providerExtensions;
    }

    /**
     * Global functions must be explicitly marked as `global` before another extension can import them.
     *
     * @param {Object} functionMetadata
     * @param {string} importPath
     * @returns {boolean}
     * @private
     */
    hasGlobalFunctionAccess(functionMetadata, importPath, providerExtension) {
      if ((providerExtension && providerExtension.id === this.extension.id)
        || functionMetadata.referenceable === Constants.ExtensionReferenceable.GLOBAL) {
        return true;
      }

      logger.error('Unable to access the module with path', importPath, 'from current extension',
        this.extension.id);
      return false;
    }

    /**
     * Cache metadata resolved from dependency providers so the proxy/access checks can reuse it later.
     *
     * @param {Extension} requestingExtension
     * @param {string} alias
     * @param {Extension} resolvedProvider
     * @param {Object} functionMetadata
     * @private
     */
    static cacheResolvedGlobalFunctionMetadata(requestingExtension, alias, resolvedProvider, functionMetadata) {
      if (!resolvedProvider || resolvedProvider === requestingExtension) {
        return;
      }

      const { extensionResources } = requestingExtension;
      extensionResources.globalFunctionProviderMetadata = extensionResources.globalFunctionProviderMetadata || {};
      const { globalFunctionProviderMetadata } = extensionResources;
      globalFunctionProviderMetadata[alias] = {
        providerExtension: resolvedProvider,
        functionMetadata,
      };
    }

    /**
     * Returns global function metadata either from the host application registry or the dependency cache.
     *
     * @param {Extension} requestingExtension
     * @param {string} alias
     * @returns {{providerExtension: Extension, functionMetadata: Object}|undefined}
     * @private
     */
    static getGlobalFunctionMetadataEntry(requestingExtension, alias) {
      const { extensionResources } = requestingExtension;
      const globalFunctionsMetadata = extensionResources.globalFunctionsMetadata || {};
      if (globalFunctionsMetadata[alias]) {
        return {
          providerExtension: requestingExtension,
          functionMetadata: globalFunctionsMetadata[alias],
        };
      }

      const globalFunctionProviderMetadata = extensionResources.globalFunctionProviderMetadata || {};
      return globalFunctionProviderMetadata[alias];
    }

    /**
     * Finds an invalid use of `referenceable: "global"` for non web-application resources/functions metadata.
     *
     * @param {string} fileName
     * @param {Object} fileDef
     * @returns {{scope: string, name: string}|undefined}
     * @private
     */
    static getInvalidGlobalReferenceable(fileName, fileDef) {
      if (fileDef.referenceable === Constants.ExtensionReferenceable.GLOBAL) {
        return { scope: 'file', name: fileName };
      }

      const invalidFunctionName = Object.keys(fileDef.functions || {}).find(
        (functionName) => fileDef.functions[functionName].referenceable
          === Constants.ExtensionReferenceable.GLOBAL,
      );
      if (invalidFunctionName) {
        return { scope: 'function', name: `${fileName}.${invalidFunctionName}` };
      }

      return undefined;
    }

    /**
     * Resolves a `global:$functions/...` alias from the application-level provider list.
     *
     * @param {object} options
     * @param {string} options.alias
     * @param {string} options.importPath
     * @param {Application} options.application
     * @param {Extension} options.metadataOwnerExtension
     * @param {boolean} options.initializeProviders initialize providers refreshed by full-manifest recovery
     * @returns {Promise<string|undefined>}
     * @private
     */
    resolveGlobalFunctionPathFromProviders({
      alias,
      importPath,
      application,
      metadataOwnerExtension,
      initializeProviders,
    }) {
      const providerExtensions = Container.getGlobalFunctionProviderExtensions(application);
      const metadataFolder = Container.getFunctionMetadataFolder(
        metadataOwnerExtension,
        Constants.GLOBAL_PREFIX,
      );
      const metadataFilePath = `${metadataFolder}/functions.json`;
      const findMatchingProvider = (providerIndex) => {
        const candidateProvider = providerExtensions[providerIndex];
        if (!candidateProvider) {
          logger.error('Unable to locate function', alias, 'in path', importPath);
          return Promise.resolve(undefined);
        }

        return Promise.resolve()
          .then(() => {
            // Skip candidates that do not declare global function metadata before initialization. Initializing
            // unrelated extensions can fail before a later provider has a chance to satisfy the import.
            if (!Container.validateFunctionManifestPath(
              candidateProvider,
              metadataFilePath,
              importPath,
              undefined,
              true,
            )) {
              return findMatchingProvider(providerIndex + 1);
            }

            return this.getFunctionsMetadata(candidateProvider, metadataFolder, Constants.GLOBAL_PREFIX)
              .then((functionFiles) => {
                const functionMetadata = functionFiles[alias];
                if (!functionMetadata) {
                  return findMatchingProvider(providerIndex + 1);
                }

                if (!this.hasGlobalFunctionAccess(functionMetadata, importPath, candidateProvider)) {
                  return undefined;
                }

                return Promise.resolve()
                  .then(() => (initializeProviders && typeof candidateProvider.init === 'function'
                    ? candidateProvider.init() : undefined))
                  .then(() => {
                    Container.cacheResolvedGlobalFunctionMetadata(
                      metadataOwnerExtension,
                      alias,
                      candidateProvider,
                      functionMetadata,
                    );
                    return Container.validateFunctionManifestPath(
                      candidateProvider,
                      functionMetadata.relativePath,
                      importPath,
                    ) && functionMetadata.path;
                  }, (error) => {
                    const providerId = candidateProvider.id || candidateProvider.baseUrl
                      || Constants.ExtensionFolders.BASE;
                    logger.warn(
                      `Failed to initialize global function provider '${providerId}' while resolving '${importPath}'`,
                      error,
                    );
                    return findMatchingProvider(providerIndex + 1);
                  });
              })
              .catch((error) => {
                if (HttpError.isFileNotFound(error)) {
                  return findMatchingProvider(providerIndex + 1);
                }
                throw error;
              });
          });
      };

      return findMatchingProvider(0);
    }

    /**
     * loads the 'functions' metadata descriptor once for the extension level resources.
     * @param {Extension} ext the extension whose functions resources are to be loaded
     * dependent of the current extension.
     * @param {string} funcMetadataPath relative path to the folder for functions.json
     * @returns {Promise<Object>|*}
     */
    getFunctionsMetadata(ext, funcMetadataPath, providerType = 'extension') {
      const extension = ext;
      const funcsFolder = `${extension.baseUrl}${funcMetadataPath}`;
      const funcsJsonFilePath = `${funcsFolder}/functions.json`;
      const metadataKey = providerType === Constants.GLOBAL_PREFIX ? 'globalFunctionsMetadata' : 'functionsMetadata';

      if (extension.extensionResources && extension.extensionResources[metadataKey]) {
        // if no functions were declared then it's set to an empty metadata
        return Promise.resolve(extension.extensionResources[metadataKey]);
      }

      return this.application.runtimeEnvironment.getTextResource(funcsJsonFilePath)
        .then((funcsJSONResource) => {
          // parse JSON content and load additional function JS files.
          const jsonContent = Utils.parseJsonResource(funcsJSONResource);
          const funcFilesDef = {}; // stores the loaded layoutFunction files

          // check the extension for the alias-ed function JS, to get the actual resource path
          Object.keys(jsonContent.files || {})
            .forEach((fileName) => {
              const fileDef = jsonContent.files[fileName];
              const invalidGlobalReferenceable = funcMetadataPath !== 'resources/functions'
                && Container.getInvalidGlobalReferenceable(fileName, fileDef);
              if (invalidGlobalReferenceable) {
                throw new Error('The referenceable \'global\' is only valid for web application '
                  + `resources/functions definitions. Remove it from the ${invalidGlobalReferenceable.scope} `
                  + `'${invalidGlobalReferenceable.name}' in '${funcsJsonFilePath}'.`);
              }
              const fileDefAlt = {};
              const funcsDefAlt = {};

              // fileName is an alias and can be different from file name in path
              fileDefAlt.fileName = fileName;
              fileDefAlt.path = `${funcsFolder}/${fileDef.path}`;
              const hasJsExt = (fileDef.path).endsWith('.js');
              const relPathInExt = `${funcMetadataPath}/${fileDef.path}`;
              fileDefAlt.relativePath = hasJsExt ? relPathInExt : `${relPathInExt}.js`;
              fileDefAlt.referenceable = fileDef.referenceable || Constants.ExtensionReferenceable.SELF;
              Object.keys(fileDef.functions || [])
                .forEach((funcky) => {
                  const funcDef = fileDef.functions[funcky];
                  funcsDefAlt[funcky] = {
                    referenceable: funcDef.referenceable,
                    methodName: funcky,
                  };
                });
              fileDefAlt.functions = funcsDefAlt || fileDef.functions || {};
              funcFilesDef[fileDefAlt.fileName] = fileDefAlt;
            });

          extension.extensionResources[metadataKey] = funcFilesDef;
          return extension.extensionResources[metadataKey];
        });
    }
  }

  return Container;
});

